import { baseApi as api } from './baseApi'
export const addTagTypes = [
    'Alarm',
    'ApiCustomer',
    'Article',
    'Auth',
    'ChargePoint',
    'ChargePointData',
    'ChargingSession',
    'CircuitBoard',
    'Company',
    'CompanyTransfer',
    'ComputedNfcAccess',
    'CountrySettings',
    'ExternalServer',
    'Firmware',
    'GreenCharging',
    'Installation',
    'LoadBalancerGroup',
    'Log',
    'MeasurementDevices',
    'Migration',
    'NfcTag',
    'Notification',
    'OcppCommand',
    'Partner',
    'Pool',
    'Product',
    'Report',
    'Schedule',
    'SimulatedChargePoint',
    'SmartChargingSchedule',
    'Statistics',
    'Troubleshooting',
    'User',
] as const
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            alarmGetAll: build.query<AlarmGetAllApiResponse, AlarmGetAllApiArg>({
                query: (queryArg) => ({
                    url: `/alarms`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems, expand: queryArg.expand },
                }),
                providesTags: ['Alarm'],
            }),
            apiCustomerGetHashedApiKey: build.query<
                ApiCustomerGetHashedApiKeyApiResponse,
                ApiCustomerGetHashedApiKeyApiArg
            >({
                query: () => ({ url: `/apicustomers/generatekey` }),
                providesTags: ['ApiCustomer'],
            }),
            apiCustomerGetAll: build.query<ApiCustomerGetAllApiResponse, ApiCustomerGetAllApiArg>({
                query: (queryArg) => ({
                    url: `/apicustomers`,
                    params: { Enabled: queryArg.enabled, CallbackEnabled: queryArg.callbackEnabled },
                }),
                providesTags: ['ApiCustomer'],
            }),
            apiCustomerCreate: build.mutation<ApiCustomerCreateApiResponse, ApiCustomerCreateApiArg>({
                query: (queryArg) => ({ url: `/apicustomers`, method: 'POST', body: queryArg.apiCustomer }),
                invalidatesTags: ['ApiCustomer'],
            }),
            apiCustomerUpdate: build.mutation<ApiCustomerUpdateApiResponse, ApiCustomerUpdateApiArg>({
                query: (queryArg) => ({ url: `/apicustomers`, method: 'PUT', body: queryArg.apiCustomer }),
                invalidatesTags: ['ApiCustomer'],
            }),
            apiCustomerDelete: build.mutation<ApiCustomerDeleteApiResponse, ApiCustomerDeleteApiArg>({
                query: (queryArg) => ({ url: `/apicustomers/${queryArg.apiCustomerId}`, method: 'DELETE' }),
                invalidatesTags: ['ApiCustomer'],
            }),
            apiCustomerGetChargePoints: build.query<
                ApiCustomerGetChargePointsApiResponse,
                ApiCustomerGetChargePointsApiArg
            >({
                query: (queryArg) => ({ url: `/apicustomers/${queryArg.apiCustomerId}/chargepointids` }),
                providesTags: ['ApiCustomer'],
            }),
            articleGetFilteredArticles: build.query<
                ArticleGetFilteredArticlesApiResponse,
                ArticleGetFilteredArticlesApiArg
            >({
                query: (queryArg) => ({ url: `/articles`, params: { ProductId: queryArg.productId } }),
                providesTags: ['Article'],
            }),
            authLogin: build.mutation<AuthLoginApiResponse, AuthLoginApiArg>({
                query: (queryArg) => ({ url: `/auth/login`, method: 'POST', body: queryArg.userCredentials }),
                invalidatesTags: ['Auth'],
            }),
            authRegister: build.mutation<AuthRegisterApiResponse, AuthRegisterApiArg>({
                query: (queryArg) => ({
                    url: `/auth/register`,
                    method: 'POST',
                    body: queryArg.chargePointUser,
                    params: { hostUrl: queryArg.hostUrl },
                }),
                invalidatesTags: ['Auth'],
            }),
            authVerifyEmail: build.mutation<AuthVerifyEmailApiResponse, AuthVerifyEmailApiArg>({
                query: (queryArg) => ({ url: `/auth/email/verify/${queryArg.token}`, method: 'PUT' }),
                invalidatesTags: ['Auth'],
            }),
            authAcceptInvitation: build.mutation<AuthAcceptInvitationApiResponse, AuthAcceptInvitationApiArg>({
                query: (queryArg) => ({ url: `/auth/invitation/accept/${queryArg.token}`, method: 'PUT' }),
                invalidatesTags: ['Auth'],
            }),
            authRequestPasswordReset: build.mutation<
                AuthRequestPasswordResetApiResponse,
                AuthRequestPasswordResetApiArg
            >({
                query: (queryArg) => ({
                    url: `/auth/password/reset/request`,
                    method: 'PUT',
                    body: queryArg.userCredentials,
                    params: { hostUrl: queryArg.hostUrl },
                }),
                invalidatesTags: ['Auth'],
            }),
            authRequestPassword: build.mutation<AuthRequestPasswordApiResponse, AuthRequestPasswordApiArg>({
                query: (queryArg) => ({
                    url: `/auth/password/reset/${queryArg.token}`,
                    method: 'PUT',
                    body: queryArg.userCredentials,
                }),
                invalidatesTags: ['Auth'],
            }),
            authRegisterPartnerCandidate: build.mutation<
                AuthRegisterPartnerCandidateApiResponse,
                AuthRegisterPartnerCandidateApiArg
            >({
                query: (queryArg) => ({
                    url: `/auth/${queryArg.userId}/register/partner/candidate`,
                    method: 'POST',
                    body: queryArg.partner,
                }),
                invalidatesTags: ['Auth'],
            }),
            chargePointUpdate: build.mutation<ChargePointUpdateApiResponse, ChargePointUpdateApiArg>({
                query: (queryArg) => ({ url: `/chargepoints`, method: 'PUT', body: queryArg.chargePoint }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointUpdateChargerInformation: build.mutation<
                ChargePointUpdateChargerInformationApiResponse,
                ChargePointUpdateChargerInformationApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/chargerInformation`,
                    method: 'PUT',
                    body: queryArg.chargerInformation,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointCreateFromTemplate: build.mutation<
                ChargePointCreateFromTemplateApiResponse,
                ChargePointCreateFromTemplateApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.monitorArticleId}/create`,
                    method: 'POST',
                    params: { processorId: queryArg.processorId },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetChargePoints: build.query<
                ChargePointGetChargePointsApiResponse,
                ChargePointGetChargePointsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/paged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        OcppOnly: queryArg.ocppOnly,
                        ProductId: queryArg.productId,
                        PartnerId: queryArg.partnerId,
                        CompanyId: queryArg.companyId,
                        CompanyName: queryArg.companyName,
                        InstalledFirmwareVersion: queryArg.installedFirmwareVersion,
                        FirmwareType: queryArg.firmwareType,
                        ImsiNumber: queryArg.imsiNumber,
                        OwnerName: queryArg.ownerName,
                        OwnerEmail: queryArg.ownerEmail,
                        OwnerMobile: queryArg.ownerMobile,
                        Status: queryArg.status,
                        ErrorCode: queryArg.errorCode,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                        Column: queryArg.column,
                        SortOrder: queryArg.sortOrder,
                        expand: queryArg.expand,
                    },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGet: build.query<ChargePointGetApiResponse, ChargePointGetApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}`,
                    headers: { wifiPassword: queryArg.wifiPassword },
                    params: { expand: queryArg.expand },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGetPartner: build.query<ChargePointGetPartnerApiResponse, ChargePointGetPartnerApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/partner` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointSaveCalibration: build.mutation<
                ChargePointSaveCalibrationApiResponse,
                ChargePointSaveCalibrationApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/calibration`,
                    method: 'POST',
                    body: queryArg.chargePointCalibration,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointSetupCallback: build.mutation<
                ChargePointSetupCallbackApiResponse,
                ChargePointSetupCallbackApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/callback`,
                    method: 'PUT',
                    body: queryArg.setupCallback,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetCalibration: build.query<
                ChargePointGetCalibrationApiResponse,
                ChargePointGetCalibrationApiArg
            >({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/calibration` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGetNewest: build.query<ChargePointGetNewestApiResponse, ChargePointGetNewestApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/newest`, params: { productId: queryArg.productId } }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGetChargingSessionsWithCount: build.query<
                ChargePointGetChargingSessionsWithCountApiResponse,
                ChargePointGetChargingSessionsWithCountApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/chargingsessions`,
                    params: { filterZero: queryArg.filterZero, Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointDeleteChargingSessions: build.mutation<
                ChargePointDeleteChargingSessionsApiResponse,
                ChargePointDeleteChargingSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/chargingsessions`,
                    method: 'DELETE',
                    params: { startTime: queryArg.startTime, endTime: queryArg.endTime },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointAddToPool: build.mutation<ChargePointAddToPoolApiResponse, ChargePointAddToPoolApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/pools/${queryArg.poolId}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointSetOwner: build.mutation<ChargePointSetOwnerApiResponse, ChargePointSetOwnerApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/users/${queryArg.userId}/owner/${queryArg.name}/${queryArg.wifiPassword}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointRemoveOwner: build.mutation<ChargePointRemoveOwnerApiResponse, ChargePointRemoveOwnerApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/owner`, method: 'DELETE' }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetManual: build.query<ChargePointGetManualApiResponse, ChargePointGetManualApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/manual` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointReboot: build.mutation<ChargePointRebootApiResponse, ChargePointRebootApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/reboot`, method: 'PUT' }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetRemoteStartTags: build.query<
                ChargePointGetRemoteStartTagsApiResponse,
                ChargePointGetRemoteStartTagsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/${queryArg.connectorId}/remotestart/tags`,
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointRemoteStart: build.mutation<ChargePointRemoteStartApiResponse, ChargePointRemoteStartApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/${queryArg.connectorId}/remotestart`,
                    method: 'PUT',
                    params: { rfidTag: queryArg.rfidTag },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointRemoteStop: build.mutation<ChargePointRemoteStopApiResponse, ChargePointRemoteStopApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/${queryArg.connectorId}/remotestop`,
                    method: 'PUT',
                    params: { userId: queryArg.userId },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointOverrideScheduleForOngoingChargingSession: build.mutation<
                ChargePointOverrideScheduleForOngoingChargingSessionApiResponse,
                ChargePointOverrideScheduleForOngoingChargingSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/${queryArg.connectorId}/schedule/override`,
                    method: 'PUT',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetScheduleOverrideStatus: build.query<
                ChargePointGetScheduleOverrideStatusApiResponse,
                ChargePointGetScheduleOverrideStatusApiArg
            >({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/schedule/override/status` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointStartTest: build.mutation<ChargePointStartTestApiResponse, ChargePointStartTestApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/starttest`, method: 'PUT' }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointSetProductionConfiguration: build.mutation<
                ChargePointSetProductionConfigurationApiResponse,
                ChargePointSetProductionConfigurationApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/configuration`,
                    method: 'PUT',
                    body: queryArg.chargePoint,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointChangeProcessorId: build.mutation<
                ChargePointChangeProcessorIdApiResponse,
                ChargePointChangeProcessorIdApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/processorid`,
                    method: 'PUT',
                    body: queryArg.changeProcessorId,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetMaintenanceLogs: build.query<
                ChargePointGetMaintenanceLogsApiResponse,
                ChargePointGetMaintenanceLogsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/maintenancelogs`,
                    params: {
                        Offset: queryArg.offset,
                        NoOfItems: queryArg.noOfItems,
                        accessLevel: queryArg.accessLevel,
                    },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointAddMaintenanceLog: build.mutation<
                ChargePointAddMaintenanceLogApiResponse,
                ChargePointAddMaintenanceLogApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/maintenancelog`,
                    method: 'POST',
                    body: queryArg.maintenanceLog,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetAlarms: build.query<ChargePointGetAlarmsApiResponse, ChargePointGetAlarmsApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/alarms`,
                    params: { showHidden: queryArg.showHidden, expand: queryArg.expand },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointAcknowledgeAlarm: build.mutation<
                ChargePointAcknowledgeAlarmApiResponse,
                ChargePointAcknowledgeAlarmApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/alarms/${queryArg.alarmId}/acknowledge`,
                    method: 'POST',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointDeleteRejectedTag: build.mutation<
                ChargePointDeleteRejectedTagApiResponse,
                ChargePointDeleteRejectedTagApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/nfctags/rejected/${queryArg.nfcTagId}/delete`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointAddChargePointProductionInfo: build.mutation<
                ChargePointAddChargePointProductionInfoApiResponse,
                ChargePointAddChargePointProductionInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/productioninfo`,
                    method: 'POST',
                    body: queryArg.chargePointProductionInfo,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetLatestChargePointProductionInfo: build.query<
                ChargePointGetLatestChargePointProductionInfoApiResponse,
                ChargePointGetLatestChargePointProductionInfoApiArg
            >({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/productioninfo` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGetChargePointProductionInfo: build.query<
                ChargePointGetChargePointProductionInfoApiResponse,
                ChargePointGetChargePointProductionInfoApiArg
            >({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/productioninfos` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointDisableOcpp: build.mutation<ChargePointDisableOcppApiResponse, ChargePointDisableOcppApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocpp/disable`,
                    method: 'POST',
                    params: { forceUpdate: queryArg.forceUpdate },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointEnableOcpp: build.mutation<ChargePointEnableOcppApiResponse, ChargePointEnableOcppApiArg>({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocpp/enable`,
                    method: 'POST',
                    params: { forceUpdate: queryArg.forceUpdate },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointChangeOcppService: build.mutation<
                ChargePointChangeOcppServiceApiResponse,
                ChargePointChangeOcppServiceApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocpp/services/change/${queryArg.ocppProxyServiceId}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointSetOcppService: build.mutation<
                ChargePointSetOcppServiceApiResponse,
                ChargePointSetOcppServiceApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocpp/services/set/${queryArg.ocppProxyServiceId}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointUpdateFirmware: build.mutation<
                ChargePointUpdateFirmwareApiResponse,
                ChargePointUpdateFirmwareApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/firmware/update`,
                    method: 'POST',
                    body: queryArg.updateChargerFirmwareOnSeveralModel,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointUpdateFirmware2: build.mutation<
                ChargePointUpdateFirmware2ApiResponse,
                ChargePointUpdateFirmware2ApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/firmware/update`,
                    method: 'POST',
                    body: queryArg.updateChargerFirmwareModel,
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetOcppCommunicationLogs: build.query<
                ChargePointGetOcppCommunicationLogsApiResponse,
                ChargePointGetOcppCommunicationLogsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocppcommunicationlogs`,
                    method: 'POST',
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems, actions: queryArg.actions },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointGetOcppRelayLogs: build.query<
                ChargePointGetOcppRelayLogsApiResponse,
                ChargePointGetOcppRelayLogsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/ocppproxyorrelaylogs`,
                    method: 'POST',
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems, actions: queryArg.actions },
                }),
                providesTags: ['ChargePoint'],
            }),
            chargePointReset: build.mutation<ChargePointResetApiResponse, ChargePointResetApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/reset`, method: 'PUT' }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointResetAvailableFirmwareVersion: build.mutation<
                ChargePointResetAvailableFirmwareVersionApiResponse,
                ChargePointResetAvailableFirmwareVersionApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/resetfirmwareversion`,
                    method: 'POST',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointGetStatusIntervals: build.query<
                ChargePointGetStatusIntervalsApiResponse,
                ChargePointGetStatusIntervalsApiArg
            >({
                query: () => ({ url: `/chargepoints/statusintervals` }),
                providesTags: ['ChargePoint'],
            }),
            chargePointUpdateChargePointUserSettings: build.mutation<
                ChargePointUpdateChargePointUserSettingsApiResponse,
                ChargePointUpdateChargePointUserSettingsApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/updateusersettings`,
                    method: 'PUT',
                    params: {
                        name: queryArg.name,
                        userCurrentConnector1: queryArg.userCurrentConnector1,
                        userCurrentConnector2: queryArg.userCurrentConnector2,
                    },
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointClearCache: build.mutation<ChargePointClearCacheApiResponse, ChargePointClearCacheApiArg>({
                query: (queryArg) => ({ url: `/chargepoints/${queryArg.chargePointId}/cache/clear`, method: 'PUT' }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointUpdateConnectorAvailability: build.mutation<
                ChargePointUpdateConnectorAvailabilityApiResponse,
                ChargePointUpdateConnectorAvailabilityApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepoints/${queryArg.chargePointId}/${queryArg.connectorId}/changeavailability/${queryArg.availability}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['ChargePoint'],
            }),
            chargePointDataGetLatestChargePointData: build.query<
                ChargePointDataGetLatestChargePointDataApiResponse,
                ChargePointDataGetLatestChargePointDataApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepointdata/${queryArg.chargePointId}/latestitem`,
                    params: { dataTypeId: queryArg.dataTypeId },
                }),
                providesTags: ['ChargePointData'],
            }),
            chargePointDataGetChargePointDataWithFilter: build.query<
                ChargePointDataGetChargePointDataWithFilterApiResponse,
                ChargePointDataGetChargePointDataWithFilterApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepointdata/${queryArg.chargePointId}`,
                    method: 'POST',
                    body: queryArg.dataLogFilter,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['ChargePointData'],
            }),
            chargePointDataGetChargePointData: build.query<
                ChargePointDataGetChargePointDataApiResponse,
                ChargePointDataGetChargePointDataApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargepointdata/${queryArg.chargePointId}`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['ChargePointData'],
            }),
            chargingSessionGetConsumptionForSession: build.query<
                ChargingSessionGetConsumptionForSessionApiResponse,
                ChargingSessionGetConsumptionForSessionApiArg
            >({
                query: (queryArg) => ({ url: `/chargingsessions/${queryArg.id}/consumption` }),
                providesTags: ['ChargingSession'],
            }),
            chargingSessionGetChargingSessionsForCurrentUser: build.query<
                ChargingSessionGetChargingSessionsForCurrentUserApiResponse,
                ChargingSessionGetChargingSessionsForCurrentUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargingsessions`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['ChargingSession'],
            }),
            chargingSessionGetChargingSessionsForLoggedInUser: build.query<
                ChargingSessionGetChargingSessionsForLoggedInUserApiResponse,
                ChargingSessionGetChargingSessionsForLoggedInUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/chargingsessions/${queryArg.userId}`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['ChargingSession'],
            }),
            chargingSessionDeleteChargingSession: build.mutation<
                ChargingSessionDeleteChargingSessionApiResponse,
                ChargingSessionDeleteChargingSessionApiArg
            >({
                query: (queryArg) => ({ url: `/chargingsessions/${queryArg.id}`, method: 'DELETE' }),
                invalidatesTags: ['ChargingSession'],
            }),
            circuitBoardCreateCircuitBoard: build.mutation<
                CircuitBoardCreateCircuitBoardApiResponse,
                CircuitBoardCreateCircuitBoardApiArg
            >({
                query: (queryArg) => ({ url: `/circuitboards`, method: 'POST', body: queryArg.circuitBoard }),
                invalidatesTags: ['CircuitBoard'],
            }),
            circuitBoardGetMacAddress: build.mutation<
                CircuitBoardGetMacAddressApiResponse,
                CircuitBoardGetMacAddressApiArg
            >({
                query: (queryArg) => ({
                    url: `/circuitboards/${queryArg.chargePointId}/mac`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['CircuitBoard'],
            }),
            circuitBoardSetExternalOcppServer: build.mutation<
                CircuitBoardSetExternalOcppServerApiResponse,
                CircuitBoardSetExternalOcppServerApiArg
            >({
                query: (queryArg) => ({
                    url: `/circuitboards/${queryArg.chargePointId}/externalserver/${queryArg.externalServerId}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['CircuitBoard'],
            }),
            companyGetAllOrFiltered: build.query<CompanyGetAllOrFilteredApiResponse, CompanyGetAllOrFilteredApiArg>({
                query: (queryArg) => ({
                    url: `/companies`,
                    params: {
                        IsPartnerRestricted: queryArg.isPartnerRestricted,
                        NumberOfItems: queryArg.numberOfItems,
                        Name: queryArg.name,
                        PartnerName: queryArg.partnerName,
                        PartnerId: queryArg.partnerId,
                    },
                }),
                providesTags: ['Company'],
            }),
            companyCreateCompany: build.mutation<CompanyCreateCompanyApiResponse, CompanyCreateCompanyApiArg>({
                query: (queryArg) => ({ url: `/companies`, method: 'POST', body: queryArg.company }),
                invalidatesTags: ['Company'],
            }),
            companyUpdateCompany: build.mutation<CompanyUpdateCompanyApiResponse, CompanyUpdateCompanyApiArg>({
                query: (queryArg) => ({ url: `/companies`, method: 'PUT', body: queryArg.company }),
                invalidatesTags: ['Company'],
            }),
            companyGetCompanyById: build.query<CompanyGetCompanyByIdApiResponse, CompanyGetCompanyByIdApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}` }),
                providesTags: ['Company'],
            }),
            companyDeleteCompany: build.mutation<CompanyDeleteCompanyApiResponse, CompanyDeleteCompanyApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}`, method: 'DELETE' }),
                invalidatesTags: ['Company'],
            }),
            companyGetAdministratedCompanies: build.query<
                CompanyGetAdministratedCompaniesApiResponse,
                CompanyGetAdministratedCompaniesApiArg
            >({
                query: () => ({ url: `/companies/users/admin` }),
                providesTags: ['Company'],
            }),
            companyGetUsers: build.query<CompanyGetUsersApiResponse, CompanyGetUsersApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/users` }),
                providesTags: ['Company'],
            }),
            companyCreateUser: build.mutation<CompanyCreateUserApiResponse, CompanyCreateUserApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users`,
                    method: 'POST',
                    body: queryArg.chargePointUser,
                }),
                invalidatesTags: ['Company'],
            }),
            companyUpdateUser: build.mutation<CompanyUpdateUserApiResponse, CompanyUpdateUserApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users`,
                    method: 'PUT',
                    body: queryArg.chargePointUser,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetChargePointsPaged: build.query<
                CompanyGetChargePointsPagedApiResponse,
                CompanyGetChargePointsPagedApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargepointspaged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        OcppOnly: queryArg.ocppOnly,
                        ProductId: queryArg.productId,
                        PartnerId: queryArg.partnerId,
                        CompanyId: queryArg.CompanyId,
                        CompanyName: queryArg.companyName,
                        InstalledFirmwareVersion: queryArg.installedFirmwareVersion,
                        FirmwareType: queryArg.firmwareType,
                        ImsiNumber: queryArg.imsiNumber,
                        OwnerName: queryArg.ownerName,
                        OwnerEmail: queryArg.ownerEmail,
                        OwnerMobile: queryArg.ownerMobile,
                        Status: queryArg.status,
                        ErrorCode: queryArg.errorCode,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                        Column: queryArg.column,
                        SortOrder: queryArg.sortOrder,
                        expand: queryArg.expand,
                    },
                }),
                providesTags: ['Company'],
            }),
            companyGetPartner: build.query<CompanyGetPartnerApiResponse, CompanyGetPartnerApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/partner` }),
                providesTags: ['Company'],
            }),
            companyGetReportDeliverySchedules: build.query<
                CompanyGetReportDeliverySchedulesApiResponse,
                CompanyGetReportDeliverySchedulesApiArg
            >({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/reportdeliveryschedules` }),
                providesTags: ['Company'],
            }),
            companyCreateReportDeliverySchedule: build.mutation<
                CompanyCreateReportDeliveryScheduleApiResponse,
                CompanyCreateReportDeliveryScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/reportdeliveryschedules`,
                    method: 'POST',
                    body: queryArg.reportDeliverySchedule,
                }),
                invalidatesTags: ['Company'],
            }),
            companyUpdateReportDeliverySchedule: build.mutation<
                CompanyUpdateReportDeliveryScheduleApiResponse,
                CompanyUpdateReportDeliveryScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/reportdeliveryschedules`,
                    method: 'PUT',
                    body: queryArg.reportDeliverySchedule,
                }),
                invalidatesTags: ['Company'],
            }),
            companyDeleteReportDeliverySchedule: build.mutation<
                CompanyDeleteReportDeliveryScheduleApiResponse,
                CompanyDeleteReportDeliveryScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/reportdeliveryschedules/${queryArg.reportDeliveryScheduleId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Company'],
            }),
            companyValidateImportChargepoints: build.mutation<
                CompanyValidateImportChargepointsApiResponse,
                CompanyValidateImportChargepointsApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargepoints/validateimport`,
                    method: 'POST',
                }),
                invalidatesTags: ['Company'],
            }),
            companyImportChargepoints: build.mutation<
                CompanyImportChargepointsApiResponse,
                CompanyImportChargepointsApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargepoints/import`,
                    method: 'POST',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Company'],
            }),
            companyCreateImportString: build.query<
                CompanyCreateImportStringApiResponse,
                CompanyCreateImportStringApiArg
            >({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/chargepoints/getimportstring` }),
                providesTags: ['Company'],
            }),
            companyGetPools: build.query<CompanyGetPoolsApiResponse, CompanyGetPoolsApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/pools` }),
                providesTags: ['Company'],
            }),
            companyGetChargingSessionsByRange: build.query<
                CompanyGetChargingSessionsByRangeApiResponse,
                CompanyGetChargingSessionsByRangeApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargingsessions/interval`,
                    params: { startTime: queryArg.startTime, endTime: queryArg.endTime },
                }),
                providesTags: ['Company'],
            }),
            companyGetChargingSessions: build.query<
                CompanyGetChargingSessionsApiResponse,
                CompanyGetChargingSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargingsessions`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['Company'],
            }),
            companyDeleteChargingSessions: build.mutation<
                CompanyDeleteChargingSessionsApiResponse,
                CompanyDeleteChargingSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargingsessions`,
                    method: 'DELETE',
                    params: { startTime: queryArg.startTime, endTime: queryArg.endTime },
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetNfcTags: build.query<CompanyGetNfcTagsApiResponse, CompanyGetNfcTagsApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/nfctags` }),
                providesTags: ['Company'],
            }),
            companyGetLoadbalancerGroupsPaged: build.query<
                CompanyGetLoadbalancerGroupsPagedApiResponse,
                CompanyGetLoadbalancerGroupsPagedApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/loadbalancergroups/paged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        CompanyName: queryArg.companyName,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['Company'],
            }),
            companyAddRoleToUser: build.mutation<CompanyAddRoleToUserApiResponse, CompanyAddRoleToUserApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users/${queryArg.userId}/roles`,
                    method: 'POST',
                    body: queryArg.role,
                }),
                invalidatesTags: ['Company'],
            }),
            companyUpdateRoleToUser: build.mutation<CompanyUpdateRoleToUserApiResponse, CompanyUpdateRoleToUserApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users/${queryArg.userId}/roles`,
                    method: 'PUT',
                    body: queryArg.role,
                }),
                invalidatesTags: ['Company'],
            }),
            companyAddChargePointToCompany: build.mutation<
                CompanyAddChargePointToCompanyApiResponse,
                CompanyAddChargePointToCompanyApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargepoints`,
                    method: 'PUT',
                    body: queryArg.chargePointCredentials,
                }),
                invalidatesTags: ['Company'],
            }),
            companyRemoveChargePoint: build.mutation<
                CompanyRemoveChargePointApiResponse,
                CompanyRemoveChargePointApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/chargepoints/${queryArg.chargePointId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Company'],
            }),
            companyRemoveUser: build.mutation<CompanyRemoveUserApiResponse, CompanyRemoveUserApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users/${queryArg.userId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetChargePointsWithNfcInfo: build.query<
                CompanyGetChargePointsWithNfcInfoApiResponse,
                CompanyGetChargePointsWithNfcInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/users/${queryArg.userId}/chargepoints`,
                }),
                providesTags: ['Company'],
            }),
            companySetChargePointsNfcInfo: build.mutation<
                CompanySetChargePointsNfcInfoApiResponse,
                CompanySetChargePointsNfcInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/users/${queryArg.userId}/chargepoints`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetPoolsWithNfcInfo: build.query<
                CompanyGetPoolsWithNfcInfoApiResponse,
                CompanyGetPoolsWithNfcInfoApiArg
            >({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/nfc/users/${queryArg.userId}/pools` }),
                providesTags: ['Company'],
            }),
            companySetPoolsNfcInfo: build.mutation<CompanySetPoolsNfcInfoApiResponse, CompanySetPoolsNfcInfoApiArg>({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/users/${queryArg.userId}/pools`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetChargePointsWithNfcInfoForTag: build.query<
                CompanyGetChargePointsWithNfcInfoForTagApiResponse,
                CompanyGetChargePointsWithNfcInfoForTagApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/tags/${queryArg.nfcTagId}/chargepoints`,
                }),
                providesTags: ['Company'],
            }),
            companySetChargePointsNfcInfoForTag: build.mutation<
                CompanySetChargePointsNfcInfoForTagApiResponse,
                CompanySetChargePointsNfcInfoForTagApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/tags/${queryArg.nfcTagId}/chargepoints`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetPoolsWithNfcInfoForTag: build.query<
                CompanyGetPoolsWithNfcInfoForTagApiResponse,
                CompanyGetPoolsWithNfcInfoForTagApiArg
            >({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/nfc/tags/${queryArg.nfcTagId}/pools` }),
                providesTags: ['Company'],
            }),
            companySetPoolsNfcInfoForTag: build.mutation<
                CompanySetPoolsNfcInfoForTagApiResponse,
                CompanySetPoolsNfcInfoForTagApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/nfc/tags/${queryArg.nfcTagId}/pools`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetNfcTagsForUser: build.query<CompanyGetNfcTagsForUserApiResponse, CompanyGetNfcTagsForUserApiArg>({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/users/${queryArg.userId}/nfctags` }),
                providesTags: ['Company'],
            }),
            companyGetChargePointsForUser: build.query<
                CompanyGetChargePointsForUserApiResponse,
                CompanyGetChargePointsForUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users/${queryArg.userId}/chargepoints`,
                }),
                providesTags: ['Company'],
            }),
            companyInviteExistingUserToCompany: build.mutation<
                CompanyInviteExistingUserToCompanyApiResponse,
                CompanyInviteExistingUserToCompanyApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/users/invite/${queryArg.role}`,
                    method: 'PUT',
                    body: queryArg.chargePointUser,
                    headers: { origin: queryArg.origin },
                }),
                invalidatesTags: ['Company'],
            }),
            companyAddMeasurementDeviceToCompany: build.mutation<
                CompanyAddMeasurementDeviceToCompanyApiResponse,
                CompanyAddMeasurementDeviceToCompanyApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/measurementdevices`,
                    method: 'PUT',
                    body: queryArg.measurementDeviceCredentials,
                }),
                invalidatesTags: ['Company'],
            }),
            companyGetDeviceByCompanyId: build.query<
                CompanyGetDeviceByCompanyIdApiResponse,
                CompanyGetDeviceByCompanyIdApiArg
            >({
                query: (queryArg) => ({ url: `/companies/${queryArg.companyId}/measurementdevices` }),
                providesTags: ['Company'],
            }),
            companyRemoveMeasurementDevice: build.mutation<
                CompanyRemoveMeasurementDeviceApiResponse,
                CompanyRemoveMeasurementDeviceApiArg
            >({
                query: (queryArg) => ({
                    url: `/companies/${queryArg.companyId}/measurementdevices/${queryArg.measurementDeviceId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Company'],
            }),
            companyTransferGetPartnerTransferKey: build.query<
                CompanyTransferGetPartnerTransferKeyApiResponse,
                CompanyTransferGetPartnerTransferKeyApiArg
            >({
                query: (queryArg) => ({ url: `/companytransfer/partner/${queryArg.partnerId}/transferkey` }),
                providesTags: ['CompanyTransfer'],
            }),
            companyTransferGetPartner: build.query<
                CompanyTransferGetPartnerApiResponse,
                CompanyTransferGetPartnerApiArg
            >({
                query: (queryArg) => ({
                    url: `/companytransfer/company/${queryArg.companyId}/transferkey/${queryArg.transferKey}/partner`,
                }),
                providesTags: ['CompanyTransfer'],
            }),
            companyTransferGetRequestForCompany: build.query<
                CompanyTransferGetRequestForCompanyApiResponse,
                CompanyTransferGetRequestForCompanyApiArg
            >({
                query: (queryArg) => ({ url: `/companytransfer/company/${queryArg.companyId}/requests` }),
                providesTags: ['CompanyTransfer'],
            }),
            companyTransferGetRequestsForPartner: build.query<
                CompanyTransferGetRequestsForPartnerApiResponse,
                CompanyTransferGetRequestsForPartnerApiArg
            >({
                query: (queryArg) => ({ url: `/companytransfer/partner/${queryArg.partnerId}/requests` }),
                providesTags: ['CompanyTransfer'],
            }),
            companyTransferGetRequests: build.query<
                CompanyTransferGetRequestsApiResponse,
                CompanyTransferGetRequestsApiArg
            >({
                query: () => ({ url: `/companytransfer/requests` }),
                providesTags: ['CompanyTransfer'],
            }),
            companyTransferRequestTransfer: build.mutation<
                CompanyTransferRequestTransferApiResponse,
                CompanyTransferRequestTransferApiArg
            >({
                query: (queryArg) => ({
                    url: `/companytransfer/transferkey/${queryArg.transferKey}/company/${queryArg.companyId}/request`,
                    method: 'POST',
                    params: { validToInUtc: queryArg.validToInUtc },
                }),
                invalidatesTags: ['CompanyTransfer'],
            }),
            companyTransferAcceptRequest: build.mutation<
                CompanyTransferAcceptRequestApiResponse,
                CompanyTransferAcceptRequestApiArg
            >({
                query: (queryArg) => ({
                    url: `/companytransfer/partner/${queryArg.partnerId}/company/${queryArg.companyId}/accept`,
                    method: 'PUT',
                }),
                invalidatesTags: ['CompanyTransfer'],
            }),
            companyTransferRejectRequest: build.mutation<
                CompanyTransferRejectRequestApiResponse,
                CompanyTransferRejectRequestApiArg
            >({
                query: (queryArg) => ({
                    url: `/companytransfer/partner/${queryArg.partnerId}/company/${queryArg.companyId}/reject`,
                    method: 'PUT',
                }),
                invalidatesTags: ['CompanyTransfer'],
            }),
            companyTransferBatchTransfer: build.mutation<
                CompanyTransferBatchTransferApiResponse,
                CompanyTransferBatchTransferApiArg
            >({
                query: (queryArg) => ({
                    url: `/companytransfer/partner/${queryArg.partnerId}/batchtransfer`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['CompanyTransfer'],
            }),
            computedNfcAccessGetAccessesForChargePoint: build.query<
                ComputedNfcAccessGetAccessesForChargePointApiResponse,
                ComputedNfcAccessGetAccessesForChargePointApiArg
            >({
                query: (queryArg) => ({ url: `/computednfcaccess/chargepoints/${queryArg.chargePointId}` }),
                providesTags: ['ComputedNfcAccess'],
            }),
            countrySettingsGetAllCountrySettings: build.query<
                CountrySettingsGetAllCountrySettingsApiResponse,
                CountrySettingsGetAllCountrySettingsApiArg
            >({
                query: () => ({ url: `/countriessettings` }),
                providesTags: ['CountrySettings'],
            }),
            externalServerGetAll: build.query<ExternalServerGetAllApiResponse, ExternalServerGetAllApiArg>({
                query: () => ({ url: `/externalservers` }),
                providesTags: ['ExternalServer'],
            }),
            externalServerCreate: build.mutation<ExternalServerCreateApiResponse, ExternalServerCreateApiArg>({
                query: (queryArg) => ({ url: `/externalservers`, method: 'POST', body: queryArg.externalServer }),
                invalidatesTags: ['ExternalServer'],
            }),
            externalServerGetByChargePointId: build.query<
                ExternalServerGetByChargePointIdApiResponse,
                ExternalServerGetByChargePointIdApiArg
            >({
                query: (queryArg) => ({ url: `/externalservers/${queryArg.chargePointId}` }),
                providesTags: ['ExternalServer'],
            }),
            externalServerGetChargePoints: build.query<
                ExternalServerGetChargePointsApiResponse,
                ExternalServerGetChargePointsApiArg
            >({
                query: (queryArg) => ({ url: `/externalservers/${queryArg.externalServerId}/chargepointids` }),
                providesTags: ['ExternalServer'],
            }),
            externalServerGetPartners: build.query<
                ExternalServerGetPartnersApiResponse,
                ExternalServerGetPartnersApiArg
            >({
                query: (queryArg) => ({ url: `/externalservers/${queryArg.externalServerId}/partners` }),
                providesTags: ['ExternalServer'],
            }),
            externalServerUpdatePartnerConnections: build.mutation<
                ExternalServerUpdatePartnerConnectionsApiResponse,
                ExternalServerUpdatePartnerConnectionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/externalservers/${queryArg.externalServerId}/partners`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['ExternalServer'],
            }),
            externalServerDelete: build.mutation<ExternalServerDeleteApiResponse, ExternalServerDeleteApiArg>({
                query: (queryArg) => ({ url: `/externalservers/${queryArg.externalServerId}`, method: 'DELETE' }),
                invalidatesTags: ['ExternalServer'],
            }),
            firmwareGetFirmwaresByFilter: build.query<
                FirmwareGetFirmwaresByFilterApiResponse,
                FirmwareGetFirmwaresByFilterApiArg
            >({
                query: (queryArg) => ({
                    url: `/products/${queryArg.productId}/firmwares`,
                    params: {
                        ProductId: queryArg.ProductId,
                        FirmwareType: queryArg.firmwareType,
                        IsActive: queryArg.isActive,
                        OcppEnabled: queryArg.ocppEnabled,
                        FirmwareStatuses: queryArg.firmwareStatuses,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['Firmware'],
            }),
            firmwareUploadFirmware: build.mutation<FirmwareUploadFirmwareApiResponse, FirmwareUploadFirmwareApiArg>({
                query: (queryArg) => ({
                    url: `/products/${queryArg.productId}/firmwares`,
                    method: 'POST',
                    body: queryArg.body,
                }),
                invalidatesTags: ['Firmware'],
            }),
            firmwareUpdateFirmware: build.mutation<FirmwareUpdateFirmwareApiResponse, FirmwareUpdateFirmwareApiArg>({
                query: (queryArg) => ({
                    url: `/products/firmware`,
                    method: 'PUT',
                    body: queryArg.firmwareGeneralModel,
                }),
                invalidatesTags: ['Firmware'],
            }),
            firmwareDeleteFirmware: build.mutation<FirmwareDeleteFirmwareApiResponse, FirmwareDeleteFirmwareApiArg>({
                query: (queryArg) => ({
                    url: `/products/${queryArg.productId}/firmwares/${queryArg.firmwareId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Firmware'],
            }),
            firmwareSetActiveFirmware: build.mutation<
                FirmwareSetActiveFirmwareApiResponse,
                FirmwareSetActiveFirmwareApiArg
            >({
                query: (queryArg) => ({
                    url: `/products/${queryArg.productId}/firmwares/${queryArg.firmwareId}/active`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Firmware'],
            }),
            firmwareGetActiveFirmware: build.query<
                FirmwareGetActiveFirmwareApiResponse,
                FirmwareGetActiveFirmwareApiArg
            >({
                query: (queryArg) => ({ url: `/products/${queryArg.productId}/firmwares/active` }),
                providesTags: ['Firmware'],
            }),
            greenChargingGetLoadBalancerGroup: build.query<
                GreenChargingGetLoadBalancerGroupApiResponse,
                GreenChargingGetLoadBalancerGroupApiArg
            >({
                query: (queryArg) => ({
                    url: `/greencharging/loadbalancergroup/${queryArg.loadBalancerGroupId}/greencharginggroup`,
                }),
                providesTags: ['GreenCharging'],
            }),
            greenChargingChargePointStatus: build.query<
                GreenChargingChargePointStatusApiResponse,
                GreenChargingChargePointStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/greencharging/chargepoint/${queryArg.chargePointId}/greenchargingstatus`,
                }),
                providesTags: ['GreenCharging'],
            }),
            greenChargingUpdateLoadBalancerGroup: build.mutation<
                GreenChargingUpdateLoadBalancerGroupApiResponse,
                GreenChargingUpdateLoadBalancerGroupApiArg
            >({
                query: (queryArg) => ({
                    url: `/greencharging`,
                    method: 'PUT',
                    body: queryArg.updateGreenChargingGroup,
                }),
                invalidatesTags: ['GreenCharging'],
            }),
            installationSetup: build.mutation<InstallationSetupApiResponse, InstallationSetupApiArg>({
                query: (queryArg) => ({ url: `/installation`, method: 'POST', body: queryArg.setupModel }),
                invalidatesTags: ['Installation'],
            }),
            loadBalancerGroupGetAllPaged: build.query<
                LoadBalancerGroupGetAllPagedApiResponse,
                LoadBalancerGroupGetAllPagedApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/paged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        CompanyName: queryArg.companyName,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupCreate: build.mutation<LoadBalancerGroupCreateApiResponse, LoadBalancerGroupCreateApiArg>({
                query: (queryArg) => ({ url: `/loadbalancergroups`, method: 'POST', body: queryArg.loadBalancerGroup }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupUpdate: build.mutation<LoadBalancerGroupUpdateApiResponse, LoadBalancerGroupUpdateApiArg>({
                query: (queryArg) => ({ url: `/loadbalancergroups`, method: 'PUT', body: queryArg.loadBalancerGroup }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupDelete: build.mutation<LoadBalancerGroupDeleteApiResponse, LoadBalancerGroupDeleteApiArg>({
                query: (queryArg) => ({ url: `/loadbalancergroups/${queryArg.id}`, method: 'DELETE' }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupGetById: build.query<LoadBalancerGroupGetByIdApiResponse, LoadBalancerGroupGetByIdApiArg>({
                query: (queryArg) => ({ url: `/loadbalancergroups/${queryArg.id}` }),
                providesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupGetChargePointsForGroup: build.query<
                LoadBalancerGroupGetChargePointsForGroupApiResponse,
                LoadBalancerGroupGetChargePointsForGroupApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/chargepoints`,
                    params: { expand: queryArg.expand },
                }),
                providesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupGetCurrentLimitScheme: build.query<
                LoadBalancerGroupGetCurrentLimitSchemeApiResponse,
                LoadBalancerGroupGetCurrentLimitSchemeApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/currentlimitschemes/${queryArg.currentLimitSchemeId}`,
                }),
                providesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupInsertOrUpdateCurrentLimitScheme: build.mutation<
                LoadBalancerGroupInsertOrUpdateCurrentLimitSchemeApiResponse,
                LoadBalancerGroupInsertOrUpdateCurrentLimitSchemeApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/currentlimitschemes`,
                    method: 'POST',
                    body: queryArg.currentLimitScheme,
                }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupDisableGroup: build.mutation<
                LoadBalancerGroupDisableGroupApiResponse,
                LoadBalancerGroupDisableGroupApiArg
            >({
                query: (queryArg) => ({ url: `/loadbalancergroups/${queryArg.id}/disable`, method: 'POST' }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupEnableGroup: build.mutation<
                LoadBalancerGroupEnableGroupApiResponse,
                LoadBalancerGroupEnableGroupApiArg
            >({
                query: (queryArg) => ({ url: `/loadbalancergroups/${queryArg.id}/enable`, method: 'POST' }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupValidateChargePointImports: build.mutation<
                LoadBalancerGroupValidateChargePointImportsApiResponse,
                LoadBalancerGroupValidateChargePointImportsApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/chargepoints/validateimport`,
                    method: 'POST',
                    params: { companyId: queryArg.companyId },
                }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupImportChargePoints: build.mutation<
                LoadBalancerGroupImportChargePointsApiResponse,
                LoadBalancerGroupImportChargePointsApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/chargepoints/import`,
                    method: 'POST',
                    body: queryArg.body,
                }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupImportChargePointsV2: build.mutation<
                LoadBalancerGroupImportChargePointsV2ApiResponse,
                LoadBalancerGroupImportChargePointsV2ApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/chargepoints/import/v2`,
                    method: 'POST',
                    body: queryArg.loadBalancerChargePointsImportModel,
                }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupDeleteChargePointFromGroup: build.mutation<
                LoadBalancerGroupDeleteChargePointFromGroupApiResponse,
                LoadBalancerGroupDeleteChargePointFromGroupApiArg
            >({
                query: (queryArg) => ({
                    url: `/loadbalancergroups/${queryArg.id}/chargepoints/${queryArg.chargePointId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['LoadBalancerGroup'],
            }),
            loadBalancerGroupGetSnapshotByDeviceId: build.query<
                LoadBalancerGroupGetSnapshotByDeviceIdApiResponse,
                LoadBalancerGroupGetSnapshotByDeviceIdApiArg
            >({
                query: (queryArg) => ({ url: `/loadbalancergroups/snapshot/devices/${queryArg.measurementDeviceId}` }),
                providesTags: ['LoadBalancerGroup'],
            }),
            logCreateLog: build.mutation<LogCreateLogApiResponse, LogCreateLogApiArg>({
                query: (queryArg) => ({ url: `/logs`, method: 'POST', body: queryArg.applicationLog }),
                invalidatesTags: ['Log'],
            }),
            logGetFilteredLogs: build.query<LogGetFilteredLogsApiResponse, LogGetFilteredLogsApiArg>({
                query: (queryArg) => ({
                    url: `/logs`,
                    params: {
                        ChargePointUserId: queryArg.chargePointUserId,
                        LogLevel: queryArg.logLevel,
                        Origin: queryArg.origin,
                        ChargePointId: queryArg.chargePointId,
                        Start: queryArg.start,
                        End: queryArg.end,
                        Message: queryArg.message,
                        Offset: queryArg.offset,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['Log'],
            }),
            logGetLog: build.query<LogGetLogApiResponse, LogGetLogApiArg>({
                query: (queryArg) => ({ url: `/logs/${queryArg.id}` }),
                providesTags: ['Log'],
            }),
            logGetErrors: build.query<LogGetErrorsApiResponse, LogGetErrorsApiArg>({
                query: (queryArg) => ({ url: `/logs/errors`, params: { noOfItems: queryArg.noOfItems } }),
                providesTags: ['Log'],
            }),
            logGetByLatestChargePointExcludingOcpp2: build.query<
                LogGetByLatestChargePointExcludingOcpp2ApiResponse,
                LogGetByLatestChargePointExcludingOcpp2ApiArg
            >({
                query: (queryArg) => ({
                    url: `/logs/chargepoints/${queryArg.chargePointId}`,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['Log'],
            }),
            logGetByLatestChargePointExcludingOcpp: build.query<
                LogGetByLatestChargePointExcludingOcppApiResponse,
                LogGetByLatestChargePointExcludingOcppApiArg
            >({
                query: (queryArg) => ({
                    url: `/logs/chargepoints/${queryArg.chargePointId}`,
                    method: 'POST',
                    body: queryArg.logFilter,
                    params: { Offset: queryArg.offset, NoOfItems: queryArg.noOfItems },
                }),
                providesTags: ['Log'],
            }),
            measurementDevicesGetFiltered: build.query<
                MeasurementDevicesGetFilteredApiResponse,
                MeasurementDevicesGetFilteredApiArg
            >({
                query: (queryArg) => ({
                    url: `/measurementdevices/v1`,
                    params: {
                        SerialNumber: queryArg.serialNumber,
                        Name: queryArg.name,
                        CompanyName: queryArg.companyName,
                        PartnerName: queryArg.partnerName,
                        ProductId: queryArg.productId,
                        PartnerId: queryArg.partnerId,
                        CompanyId: queryArg.companyId,
                        Status: queryArg.status,
                        Limit: queryArg.limit,
                    },
                }),
                providesTags: ['MeasurementDevices'],
            }),
            measurementDevicesCreate: build.mutation<
                MeasurementDevicesCreateApiResponse,
                MeasurementDevicesCreateApiArg
            >({
                query: (queryArg) => ({
                    url: `/measurementdevices/v1`,
                    method: 'POST',
                    body: queryArg.createMeasurementDeviceApiModel,
                }),
                invalidatesTags: ['MeasurementDevices'],
            }),
            measurementDevicesGetById: build.query<
                MeasurementDevicesGetByIdApiResponse,
                MeasurementDevicesGetByIdApiArg
            >({
                query: (queryArg) => ({ url: `/measurementdevices/v1/${queryArg.idOrSerialNumber}` }),
                providesTags: ['MeasurementDevices'],
            }),
            measurementDevicesUpdate: build.mutation<
                MeasurementDevicesUpdateApiResponse,
                MeasurementDevicesUpdateApiArg
            >({
                query: (queryArg) => ({
                    url: `/measurementdevices/v1/${queryArg.id}`,
                    method: 'PUT',
                    body: queryArg.updateMeasurementDeviceModel,
                }),
                invalidatesTags: ['MeasurementDevices'],
            }),
            measurementDevicesGetDevicesByUserId: build.query<
                MeasurementDevicesGetDevicesByUserIdApiResponse,
                MeasurementDevicesGetDevicesByUserIdApiArg
            >({
                query: (queryArg) => ({ url: `/measurementdevices/v1/users/${queryArg.userId}` }),
                providesTags: ['MeasurementDevices'],
            }),
            measurementDevicesRemoveMeasurementDeviceFromCompany: build.mutation<
                MeasurementDevicesRemoveMeasurementDeviceFromCompanyApiResponse,
                MeasurementDevicesRemoveMeasurementDeviceFromCompanyApiArg
            >({
                query: (queryArg) => ({ url: `/measurementdevices/v1/${queryArg.id}/remove`, method: 'PUT' }),
                invalidatesTags: ['MeasurementDevices'],
            }),
            measurementDevicesGetLatestMeasurement: build.query<
                MeasurementDevicesGetLatestMeasurementApiResponse,
                MeasurementDevicesGetLatestMeasurementApiArg
            >({
                query: (queryArg) => ({ url: `/measurementdevices/v1/${queryArg.id}/data/latest` }),
                providesTags: ['MeasurementDevices'],
            }),
            measurementDevicesGetMeasurementsForLatest24Hours: build.query<
                MeasurementDevicesGetMeasurementsForLatest24HoursApiResponse,
                MeasurementDevicesGetMeasurementsForLatest24HoursApiArg
            >({
                query: (queryArg) => ({ url: `/measurementdevices/v1/${queryArg.id}/data/day` }),
                providesTags: ['MeasurementDevices'],
            }),
            measurementDevicesGetValidatedMeasurementDevice: build.query<
                MeasurementDevicesGetValidatedMeasurementDeviceApiResponse,
                MeasurementDevicesGetValidatedMeasurementDeviceApiArg
            >({
                query: (queryArg) => ({
                    url: `/measurementdevices/v1/installation/getvalidated`,
                    method: 'POST',
                    body: queryArg.measurementDeviceCredentials,
                }),
                providesTags: ['MeasurementDevices'],
            }),
            migrationBackupMigrationState: build.mutation<
                MigrationBackupMigrationStateApiResponse,
                MigrationBackupMigrationStateApiArg
            >({
                query: (queryArg) => ({
                    url: `/migration/chargepoints/${queryArg.chargePointId}/createbackup`,
                    method: 'POST',
                    params: { token: queryArg.token },
                }),
                invalidatesTags: ['Migration'],
            }),
            migrationShouldMigrateSchedule: build.query<
                MigrationShouldMigrateScheduleApiResponse,
                MigrationShouldMigrateScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/migration/chargepoints/${queryArg.chargePointId}/connector/${queryArg.connectorId}/shouldmigrateschedule`,
                }),
                providesTags: ['Migration'],
            }),
            nfcTagCreate: build.mutation<NfcTagCreateApiResponse, NfcTagCreateApiArg>({
                query: (queryArg) => ({ url: `/nfctags`, method: 'POST', body: queryArg.nfcTag }),
                invalidatesTags: ['NfcTag'],
            }),
            nfcTagUpdate: build.mutation<NfcTagUpdateApiResponse, NfcTagUpdateApiArg>({
                query: (queryArg) => ({ url: `/nfctags`, method: 'PUT', body: queryArg.nfcTag }),
                invalidatesTags: ['NfcTag'],
            }),
            nfcTagConnectToUser: build.mutation<NfcTagConnectToUserApiResponse, NfcTagConnectToUserApiArg>({
                query: (queryArg) => ({
                    url: `/nfctags/${queryArg.nfcTagId}/users/${queryArg.userId}`,
                    method: 'POST',
                }),
                invalidatesTags: ['NfcTag'],
            }),
            nfcTagDisconnectFromUser: build.mutation<
                NfcTagDisconnectFromUserApiResponse,
                NfcTagDisconnectFromUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/nfctags/${queryArg.nfcTagId}/users/${queryArg.userId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['NfcTag'],
            }),
            nfcTagGetAccessedChargePointsInCompany: build.query<
                NfcTagGetAccessedChargePointsInCompanyApiResponse,
                NfcTagGetAccessedChargePointsInCompanyApiArg
            >({
                query: (queryArg) => ({
                    url: `/nfctags/${queryArg.nfcTagId}/companies/${queryArg.companyId}/chargepoints`,
                }),
                providesTags: ['NfcTag'],
            }),
            nfcTagGetAccessedPoolsInCompany: build.query<
                NfcTagGetAccessedPoolsInCompanyApiResponse,
                NfcTagGetAccessedPoolsInCompanyApiArg
            >({
                query: (queryArg) => ({ url: `/nfctags/${queryArg.nfcTagId}/companies/${queryArg.companyId}/pools` }),
                providesTags: ['NfcTag'],
            }),
            nfcTagDelete: build.mutation<NfcTagDeleteApiResponse, NfcTagDeleteApiArg>({
                query: (queryArg) => ({ url: `/nfctags/${queryArg.nfcTagId}`, method: 'DELETE' }),
                invalidatesTags: ['NfcTag'],
            }),
            nfcTagGetRejectedTags: build.query<NfcTagGetRejectedTagsApiResponse, NfcTagGetRejectedTagsApiArg>({
                query: (queryArg) => ({
                    url: `/nfctags/rejected`,
                    params: {
                        ChargePointId: queryArg.chargePointId,
                        CompanyId: queryArg.companyId,
                        PartnerId: queryArg.partnerId,
                        From: queryArg['from'],
                        To: queryArg.to,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['NfcTag'],
            }),
            notificationGetAllOrFiltered: build.query<
                NotificationGetAllOrFilteredApiResponse,
                NotificationGetAllOrFilteredApiArg
            >({
                query: (queryArg) => ({ url: `/notifications`, params: { filter: queryArg.filter } }),
                providesTags: ['Notification'],
            }),
            notificationCreateNotification: build.mutation<
                NotificationCreateNotificationApiResponse,
                NotificationCreateNotificationApiArg
            >({
                query: (queryArg) => ({ url: `/notifications`, method: 'POST', body: queryArg.notification }),
                invalidatesTags: ['Notification'],
            }),
            notificationGetById: build.query<NotificationGetByIdApiResponse, NotificationGetByIdApiArg>({
                query: (queryArg) => ({ url: `/notifications/${queryArg.id}` }),
                providesTags: ['Notification'],
            }),
            notificationUpdateNotification: build.mutation<
                NotificationUpdateNotificationApiResponse,
                NotificationUpdateNotificationApiArg
            >({
                query: (queryArg) => ({
                    url: `/notifications/${queryArg.id}`,
                    method: 'PUT',
                    body: queryArg.notification,
                }),
                invalidatesTags: ['Notification'],
            }),
            notificationDeleteNotification: build.mutation<
                NotificationDeleteNotificationApiResponse,
                NotificationDeleteNotificationApiArg
            >({
                query: (queryArg) => ({ url: `/notifications/${queryArg.id}`, method: 'DELETE' }),
                invalidatesTags: ['Notification'],
            }),
            notificationPublishNotification: build.mutation<
                NotificationPublishNotificationApiResponse,
                NotificationPublishNotificationApiArg
            >({
                query: (queryArg) => ({ url: `/notifications/${queryArg.id}/publish`, method: 'PUT' }),
                invalidatesTags: ['Notification'],
            }),
            ocppCommandReset: build.mutation<OcppCommandResetApiResponse, OcppCommandResetApiArg>({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/reset`,
                    method: 'PUT',
                    body: queryArg.resetReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandClearCache: build.mutation<OcppCommandClearCacheApiResponse, OcppCommandClearCacheApiArg>({
                query: (queryArg) => ({ url: `/ocppcommands/${queryArg.chargePointId}/clearcache`, method: 'PUT' }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandDataTransfer: build.mutation<OcppCommandDataTransferApiResponse, OcppCommandDataTransferApiArg>({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/datatransfer`,
                    method: 'PUT',
                    body: queryArg.dataTransferReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandGetConfiguration: build.mutation<
                OcppCommandGetConfigurationApiResponse,
                OcppCommandGetConfigurationApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/getconfiguration`,
                    method: 'PUT',
                    body: queryArg.getConfigurationReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandGetDiagnostics: build.mutation<
                OcppCommandGetDiagnosticsApiResponse,
                OcppCommandGetDiagnosticsApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/getdiagnostics`,
                    method: 'PUT',
                    body: queryArg.getDiagnosticsReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandUnlockConnector: build.mutation<
                OcppCommandUnlockConnectorApiResponse,
                OcppCommandUnlockConnectorApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/unlockconnector`,
                    method: 'PUT',
                    body: queryArg.unlockConnectorReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandChangeAvailability: build.mutation<
                OcppCommandChangeAvailabilityApiResponse,
                OcppCommandChangeAvailabilityApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/changeavailability`,
                    method: 'PUT',
                    body: queryArg.changeAvailabilityReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandRemoteStart: build.mutation<OcppCommandRemoteStartApiResponse, OcppCommandRemoteStartApiArg>({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/remotestart`,
                    method: 'PUT',
                    body: queryArg.remoteStartTransactionReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandRemoteStop: build.mutation<OcppCommandRemoteStopApiResponse, OcppCommandRemoteStopApiArg>({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/remotestop`,
                    method: 'PUT',
                    body: queryArg.remoteStopTransactionReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandTriggerMessage: build.mutation<
                OcppCommandTriggerMessageApiResponse,
                OcppCommandTriggerMessageApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/triggermessage`,
                    method: 'PUT',
                    body: queryArg.triggerMessageReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandUpdateFirmware: build.mutation<
                OcppCommandUpdateFirmwareApiResponse,
                OcppCommandUpdateFirmwareApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/updatefirmware`,
                    method: 'PUT',
                    body: queryArg.updateFirmwareReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandTest: build.mutation<OcppCommandTestApiResponse, OcppCommandTestApiArg>({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/test`,
                    method: 'PUT',
                    body: queryArg.testOcppCommand,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandUpdateConfigKeys: build.mutation<
                OcppCommandUpdateConfigKeysApiResponse,
                OcppCommandUpdateConfigKeysApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/configkeys`,
                    method: 'PUT',
                    body: queryArg.body,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandUpdateConfigKey: build.mutation<
                OcppCommandUpdateConfigKeyApiResponse,
                OcppCommandUpdateConfigKeyApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/configkey`,
                    method: 'PUT',
                    body: queryArg.keyValue,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandInstallCertificate: build.mutation<
                OcppCommandInstallCertificateApiResponse,
                OcppCommandInstallCertificateApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/certificate/install`,
                    method: 'PUT',
                    body: queryArg.installCertificateReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandDeleteCertificate: build.mutation<
                OcppCommandDeleteCertificateApiResponse,
                OcppCommandDeleteCertificateApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/certificate/delete`,
                    method: 'PUT',
                    body: queryArg.deleteCertificateReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            ocppCommandGetInstalledCerts: build.mutation<
                OcppCommandGetInstalledCertsApiResponse,
                OcppCommandGetInstalledCertsApiArg
            >({
                query: (queryArg) => ({
                    url: `/ocppcommands/${queryArg.chargePointId}/certificate/getinstalled`,
                    method: 'PUT',
                    body: queryArg.getInstalledCertificateIdsReq,
                }),
                invalidatesTags: ['OcppCommand'],
            }),
            partnerGetAllAll: build.query<PartnerGetAllAllApiResponse, PartnerGetAllAllApiArg>({
                query: (queryArg) => ({ url: `/partners`, params: { excludeOrphans: queryArg.excludeOrphans } }),
                providesTags: ['Partner'],
            }),
            partnerCreatePartner: build.mutation<PartnerCreatePartnerApiResponse, PartnerCreatePartnerApiArg>({
                query: (queryArg) => ({ url: `/partners`, method: 'POST', body: queryArg.partner }),
                invalidatesTags: ['Partner'],
            }),
            partnerUpdatePartner: build.mutation<PartnerUpdatePartnerApiResponse, PartnerUpdatePartnerApiArg>({
                query: (queryArg) => ({ url: `/partners`, method: 'PUT', body: queryArg.partner }),
                invalidatesTags: ['Partner'],
            }),
            partnerGetAll: build.query<PartnerGetAllApiResponse, PartnerGetAllApiArg>({
                query: (queryArg) => ({
                    url: `/partners/paged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        Email: queryArg.email,
                        ExcludeOrphans: queryArg.excludeOrphans,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['Partner'],
            }),
            partnerGetById: build.query<PartnerGetByIdApiResponse, PartnerGetByIdApiArg>({
                query: (queryArg) => ({ url: `/partners/${queryArg.partnerId}` }),
                providesTags: ['Partner'],
            }),
            partnerDelete: build.mutation<PartnerDeleteApiResponse, PartnerDeleteApiArg>({
                query: (queryArg) => ({ url: `/partners/${queryArg.partnerId}`, method: 'DELETE' }),
                invalidatesTags: ['Partner'],
            }),
            partnerGetCompaniesByPartnerId: build.query<
                PartnerGetCompaniesByPartnerIdApiResponse,
                PartnerGetCompaniesByPartnerIdApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/companies`,
                    params: { expand: queryArg.expand },
                }),
                providesTags: ['Partner'],
            }),
            partnerGetChargePointsPaged: build.query<
                PartnerGetChargePointsPagedApiResponse,
                PartnerGetChargePointsPagedApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/chargepointspaged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        OcppOnly: queryArg.ocppOnly,
                        ProductId: queryArg.productId,
                        PartnerId: queryArg.PartnerId,
                        CompanyId: queryArg.companyId,
                        CompanyName: queryArg.companyName,
                        InstalledFirmwareVersion: queryArg.installedFirmwareVersion,
                        FirmwareType: queryArg.firmwareType,
                        ImsiNumber: queryArg.imsiNumber,
                        OwnerName: queryArg.ownerName,
                        OwnerEmail: queryArg.ownerEmail,
                        OwnerMobile: queryArg.ownerMobile,
                        Status: queryArg.status,
                        ErrorCode: queryArg.errorCode,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                        Column: queryArg.column,
                        SortOrder: queryArg.sortOrder,
                        expand: queryArg.expand,
                    },
                }),
                providesTags: ['Partner'],
            }),
            partnerGetAlarmsByPartnerId: build.query<
                PartnerGetAlarmsByPartnerIdApiResponse,
                PartnerGetAlarmsByPartnerIdApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/alarms`,
                    params: {
                        showConfirmed: queryArg.showConfirmed,
                        showHidden: queryArg.showHidden,
                        Offset: queryArg.offset,
                        NoOfItems: queryArg.noOfItems,
                        expand: queryArg.expand,
                    },
                }),
                providesTags: ['Partner'],
            }),
            partnerSetAcknowledged: build.mutation<PartnerSetAcknowledgedApiResponse, PartnerSetAcknowledgedApiArg>({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/alarms/${queryArg.alarmId}/acknowledge`,
                    method: 'POST',
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerGetUsersByPartnerId: build.query<
                PartnerGetUsersByPartnerIdApiResponse,
                PartnerGetUsersByPartnerIdApiArg
            >({
                query: (queryArg) => ({ url: `/partners/${queryArg.partnerId}/users` }),
                providesTags: ['Partner'],
            }),
            partnerCreatePartnerUser: build.mutation<
                PartnerCreatePartnerUserApiResponse,
                PartnerCreatePartnerUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/users`,
                    method: 'POST',
                    body: queryArg.chargePointUser,
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerUpdatePartnerUser: build.mutation<
                PartnerUpdatePartnerUserApiResponse,
                PartnerUpdatePartnerUserApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/users`,
                    method: 'PUT',
                    body: queryArg.chargePointUser,
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerAddUserAsPartner: build.mutation<PartnerAddUserAsPartnerApiResponse, PartnerAddUserAsPartnerApiArg>({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/users/${queryArg.userId}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerRemoveUserAsPartner: build.mutation<
                PartnerRemoveUserAsPartnerApiResponse,
                PartnerRemoveUserAsPartnerApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/users/${queryArg.userId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerGetLoadbalancerGroupsPaged: build.query<
                PartnerGetLoadbalancerGroupsPagedApiResponse,
                PartnerGetLoadbalancerGroupsPagedApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/${queryArg.partnerId}/loadbalancergroups/paged`,
                    params: {
                        Id: queryArg.id,
                        Name: queryArg.name,
                        CompanyName: queryArg.companyName,
                        Page: queryArg.page,
                        NoOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['Partner'],
            }),
            partnerGetNfcTags: build.query<PartnerGetNfcTagsApiResponse, PartnerGetNfcTagsApiArg>({
                query: (queryArg) => ({ url: `/partners/${queryArg.partnerId}/nfctags` }),
                providesTags: ['Partner'],
            }),
            partnerGetExternalServers: build.query<
                PartnerGetExternalServersApiResponse,
                PartnerGetExternalServersApiArg
            >({
                query: (queryArg) => ({ url: `/partners/${queryArg.partnerId}/externalservers` }),
                providesTags: ['Partner'],
            }),
            partnerGetPartnerCandidates: build.query<
                PartnerGetPartnerCandidatesApiResponse,
                PartnerGetPartnerCandidatesApiArg
            >({
                query: () => ({ url: `/partners/candidates` }),
                providesTags: ['Partner'],
            }),
            partnerApprovePartnerCandidate: build.mutation<
                PartnerApprovePartnerCandidateApiResponse,
                PartnerApprovePartnerCandidateApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/candidates/${queryArg.partnerCandidateId}/approve`,
                    method: 'POST',
                }),
                invalidatesTags: ['Partner'],
            }),
            partnerDeletePartnerCandidate: build.mutation<
                PartnerDeletePartnerCandidateApiResponse,
                PartnerDeletePartnerCandidateApiArg
            >({
                query: (queryArg) => ({
                    url: `/partners/candidates/${queryArg.partnerCandidateId}/reject`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Partner'],
            }),
            poolCreate: build.mutation<PoolCreateApiResponse, PoolCreateApiArg>({
                query: (queryArg) => ({ url: `/pools`, method: 'POST', body: queryArg.pool }),
                invalidatesTags: ['Pool'],
            }),
            poolUpdate: build.mutation<PoolUpdateApiResponse, PoolUpdateApiArg>({
                query: (queryArg) => ({ url: `/pools`, method: 'PUT', body: queryArg.pool }),
                invalidatesTags: ['Pool'],
            }),
            poolGetById: build.query<PoolGetByIdApiResponse, PoolGetByIdApiArg>({
                query: (queryArg) => ({ url: `/pools/${queryArg.poolId}` }),
                providesTags: ['Pool'],
            }),
            poolConnectToUser: build.mutation<PoolConnectToUserApiResponse, PoolConnectToUserApiArg>({
                query: (queryArg) => ({ url: `/pools/${queryArg.poolId}/users/${queryArg.userId}`, method: 'POST' }),
                invalidatesTags: ['Pool'],
            }),
            poolDisconnectFromUser: build.mutation<PoolDisconnectFromUserApiResponse, PoolDisconnectFromUserApiArg>({
                query: (queryArg) => ({ url: `/pools/${queryArg.poolId}/users/${queryArg.userId}`, method: 'DELETE' }),
                invalidatesTags: ['Pool'],
            }),
            poolDeletePool: build.mutation<PoolDeletePoolApiResponse, PoolDeletePoolApiArg>({
                query: (queryArg) => ({
                    url: `/pools/${queryArg.poolId}/company/${queryArg.companyId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Pool'],
            }),
            poolGetChargePoints: build.query<PoolGetChargePointsApiResponse, PoolGetChargePointsApiArg>({
                query: (queryArg) => ({
                    url: `/pools/${queryArg.poolId}/chargepoints`,
                    params: { expand: queryArg.expand },
                }),
                providesTags: ['Pool'],
            }),
            productGetAll: build.query<ProductGetAllApiResponse, ProductGetAllApiArg>({
                query: (queryArg) => ({ url: `/products`, params: { productVariant: queryArg.productVariant } }),
                providesTags: ['Product'],
            }),
            productGetOptions: build.query<ProductGetOptionsApiResponse, ProductGetOptionsApiArg>({
                query: (queryArg) => ({
                    url: `/products/options`,
                    params: { productVariant: queryArg.productVariant },
                }),
                providesTags: ['Product'],
            }),
            productGetHardwareversions: build.query<
                ProductGetHardwareversionsApiResponse,
                ProductGetHardwareversionsApiArg
            >({
                query: (queryArg) => ({ url: `/products/${queryArg.productId}/hardwareversions` }),
                providesTags: ['Product'],
            }),
            productGetActiveHardwareVersion: build.query<
                ProductGetActiveHardwareVersionApiResponse,
                ProductGetActiveHardwareVersionApiArg
            >({
                query: (queryArg) => ({ url: `/products/${queryArg.productId}/hardwareversions/active` }),
                providesTags: ['Product'],
            }),
            productSetActiveHardwareVersion: build.mutation<
                ProductSetActiveHardwareVersionApiResponse,
                ProductSetActiveHardwareVersionApiArg
            >({
                query: (queryArg) => ({
                    url: `/products/${queryArg.productId}/hardwareversions/${queryArg.hardwareVersionId}/active`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Product'],
            }),
            reportGetReports: build.query<ReportGetReportsApiResponse, ReportGetReportsApiArg>({
                query: (queryArg) => ({ url: `/reports`, params: { accessLevel: queryArg.accessLevel } }),
                providesTags: ['Report'],
            }),
            reportGetReportsForAdmin: build.query<ReportGetReportsForAdminApiResponse, ReportGetReportsForAdminApiArg>({
                query: (queryArg) => ({
                    url: `/reports/admin`,
                    params: { selectedCompanyId: queryArg.selectedCompanyId },
                }),
                providesTags: ['Report'],
            }),
            reportGetReportsForPartner: build.query<
                ReportGetReportsForPartnerApiResponse,
                ReportGetReportsForPartnerApiArg
            >({
                query: (queryArg) => ({ url: `/reports/partner`, params: { partnerId: queryArg.partnerId } }),
                providesTags: ['Report'],
            }),
            reportGetReportData: build.query<ReportGetReportDataApiResponse, ReportGetReportDataApiArg>({
                query: (queryArg) => ({
                    url: `/reports/${queryArg.reportId}`,
                    params: {
                        PartnerId: queryArg.partnerId,
                        CompanyId: queryArg.companyId,
                        ChargePointUserId: queryArg.chargePointUserId,
                        TimeZone: queryArg.timeZone,
                        TimeZoneWindows: queryArg.timeZoneWindows,
                        Interval: queryArg.interval,
                        To: queryArg.to,
                        From: queryArg['from'],
                        FromInLocalTime: queryArg.fromInLocalTime,
                        ToInLocalTime: queryArg.toInLocalTime,
                    },
                }),
                providesTags: ['Report'],
            }),
            scheduleGetById: build.query<ScheduleGetByIdApiResponse, ScheduleGetByIdApiArg>({
                query: (queryArg) => ({ url: `/schedules/${queryArg.scheduleId}` }),
                providesTags: ['Schedule'],
            }),
            scheduleDeleteSchedule: build.mutation<ScheduleDeleteScheduleApiResponse, ScheduleDeleteScheduleApiArg>({
                query: (queryArg) => ({ url: `/schedules/${queryArg.scheduleId}`, method: 'DELETE' }),
                invalidatesTags: ['Schedule'],
            }),
            scheduleGetByChargePoint: build.query<ScheduleGetByChargePointApiResponse, ScheduleGetByChargePointApiArg>({
                query: (queryArg) => ({ url: `/schedules/chargepoint/${queryArg.chargePointId}` }),
                providesTags: ['Schedule'],
            }),
            scheduleCreateSchedule: build.mutation<ScheduleCreateScheduleApiResponse, ScheduleCreateScheduleApiArg>({
                query: (queryArg) => ({ url: `/schedules`, method: 'POST', body: queryArg.schedule }),
                invalidatesTags: ['Schedule'],
            }),
            scheduleUpdateSchedule: build.mutation<ScheduleUpdateScheduleApiResponse, ScheduleUpdateScheduleApiArg>({
                query: (queryArg) => ({ url: `/schedules`, method: 'PUT', body: queryArg.schedule }),
                invalidatesTags: ['Schedule'],
            }),
            scheduleCopyScheduleFromChargePoint: build.mutation<
                ScheduleCopyScheduleFromChargePointApiResponse,
                ScheduleCopyScheduleFromChargePointApiArg
            >({
                query: (queryArg) => ({ url: `/schedules/copy/${queryArg.chargePointId}`, method: 'POST' }),
                invalidatesTags: ['Schedule'],
            }),
            simulatedChargePointCreateSimulated: build.mutation<
                SimulatedChargePointCreateSimulatedApiResponse,
                SimulatedChargePointCreateSimulatedApiArg
            >({
                query: (queryArg) => ({
                    url: `/simulatedchargepoints/${queryArg.productId}/createsimulated`,
                    method: 'POST',
                }),
                invalidatesTags: ['SimulatedChargePoint'],
            }),
            smartChargingScheduleGet: build.query<SmartChargingScheduleGetApiResponse, SmartChargingScheduleGetApiArg>({
                query: (queryArg) => ({
                    url: `/smartChargingSchedules/${queryArg.chargePointId}/${queryArg.connectorId}`,
                }),
                providesTags: ['SmartChargingSchedule'],
            }),
            smartChargingScheduleClear: build.mutation<
                SmartChargingScheduleClearApiResponse,
                SmartChargingScheduleClearApiArg
            >({
                query: (queryArg) => ({
                    url: `/smartChargingSchedules/${queryArg.chargePointId}/${queryArg.connectorId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['SmartChargingSchedule'],
            }),
            smartChargingScheduleGetByChargePoint: build.query<
                SmartChargingScheduleGetByChargePointApiResponse,
                SmartChargingScheduleGetByChargePointApiArg
            >({
                query: (queryArg) => ({ url: `/smartChargingSchedules/chargepoint/${queryArg.chargePointId}` }),
                providesTags: ['SmartChargingSchedule'],
            }),
            smartChargingScheduleGetChargePointScheduleStatus: build.query<
                SmartChargingScheduleGetChargePointScheduleStatusApiResponse,
                SmartChargingScheduleGetChargePointScheduleStatusApiArg
            >({
                query: (queryArg) => ({ url: `/smartChargingSchedules/chargepoint/${queryArg.chargePointId}/status` }),
                providesTags: ['SmartChargingSchedule'],
            }),
            smartChargingScheduleUpdate: build.mutation<
                SmartChargingScheduleUpdateApiResponse,
                SmartChargingScheduleUpdateApiArg
            >({
                query: (queryArg) => ({
                    url: `/smartChargingSchedules`,
                    method: 'PUT',
                    body: queryArg.smartChargingSchedule,
                }),
                invalidatesTags: ['SmartChargingSchedule'],
            }),
            statisticsGetChargePointStatusStatsForAll: build.query<
                StatisticsGetChargePointStatusStatsForAllApiResponse,
                StatisticsGetChargePointStatusStatsForAllApiArg
            >({
                query: () => ({ url: `/stats/chargepoints` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetChargePointTotalConsumptionLastSevenDays: build.query<
                StatisticsGetChargePointTotalConsumptionLastSevenDaysApiResponse,
                StatisticsGetChargePointTotalConsumptionLastSevenDaysApiArg
            >({
                query: (queryArg) => ({ url: `/stats/chargepoints/${queryArg.chargePointId}/totalconsumption` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetChargePointStatusStatsForPartner: build.query<
                StatisticsGetChargePointStatusStatsForPartnerApiResponse,
                StatisticsGetChargePointStatusStatsForPartnerApiArg
            >({
                query: (queryArg) => ({ url: `/stats/partners/${queryArg.partnerId}/chargepoints` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetUserTotalConsumption: build.query<
                StatisticsGetUserTotalConsumptionApiResponse,
                StatisticsGetUserTotalConsumptionApiArg
            >({
                query: (queryArg) => ({
                    url: `/stats/users/${queryArg.userId}/totalconsumption`,
                    params: { interval: queryArg.interval },
                }),
                providesTags: ['Statistics'],
            }),
            statisticsGetChargePointStatusStatsForCompany: build.query<
                StatisticsGetChargePointStatusStatsForCompanyApiResponse,
                StatisticsGetChargePointStatusStatsForCompanyApiArg
            >({
                query: (queryArg) => ({ url: `/stats/companies/${queryArg.companyId}/chargepoints` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetCompanyTotalConsumption: build.query<
                StatisticsGetCompanyTotalConsumptionApiResponse,
                StatisticsGetCompanyTotalConsumptionApiArg
            >({
                query: (queryArg) => ({ url: `/stats/companies/${queryArg.companyId}/totalconsumption` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetUserUsageStatistics: build.query<
                StatisticsGetUserUsageStatisticsApiResponse,
                StatisticsGetUserUsageStatisticsApiArg
            >({
                query: (queryArg) => ({ url: `/stats/users/${queryArg.userId}` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetUserChargerUsageStatistics: build.query<
                StatisticsGetUserChargerUsageStatisticsApiResponse,
                StatisticsGetUserChargerUsageStatisticsApiArg
            >({
                query: (queryArg) => ({ url: `/stats/users/${queryArg.userId}/chargerusage` }),
                providesTags: ['Statistics'],
            }),
            statisticsGetCompanyUsageStatistics: build.query<
                StatisticsGetCompanyUsageStatisticsApiResponse,
                StatisticsGetCompanyUsageStatisticsApiArg
            >({
                query: (queryArg) => ({ url: `/stats/companies/${queryArg.companyId}` }),
                providesTags: ['Statistics'],
            }),
            troubleshootingGetErrorCodeTroubleshootingInfos: build.query<
                TroubleshootingGetErrorCodeTroubleshootingInfosApiResponse,
                TroubleshootingGetErrorCodeTroubleshootingInfosApiArg
            >({
                query: (queryArg) => ({
                    url: `/troubleshooting/errorcode/infos`,
                    params: { productId: queryArg.productId },
                }),
                providesTags: ['Troubleshooting'],
            }),
            troubleshootingGetErrorCodeTroubleshootingInfo: build.query<
                TroubleshootingGetErrorCodeTroubleshootingInfoApiResponse,
                TroubleshootingGetErrorCodeTroubleshootingInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/troubleshooting/errorcode/${queryArg.errorCode}/info`,
                    params: { productId: queryArg.productId },
                }),
                providesTags: ['Troubleshooting'],
            }),
            userGetFiltered: build.query<UserGetFilteredApiResponse, UserGetFilteredApiArg>({
                query: (queryArg) => ({
                    url: `/users`,
                    params: {
                        FirstName: queryArg.firstName,
                        LastName: queryArg.lastName,
                        Email: queryArg.email,
                        IsSuperAdmin: queryArg.isSuperAdmin,
                        noOfItems: queryArg.noOfItems,
                    },
                }),
                providesTags: ['User'],
            }),
            userUpdateLoggedInUser: build.mutation<UserUpdateLoggedInUserApiResponse, UserUpdateLoggedInUserApiArg>({
                query: (queryArg) => ({ url: `/users`, method: 'PUT', body: queryArg.chargePointUser }),
                invalidatesTags: ['User'],
            }),
            userGetUserById: build.query<UserGetUserByIdApiResponse, UserGetUserByIdApiArg>({
                query: (queryArg) => ({ url: `/users/${queryArg.userId}`, params: { companyId: queryArg.companyId } }),
                providesTags: ['User'],
            }),
            userGetUserSettings: build.query<UserGetUserSettingsApiResponse, UserGetUserSettingsApiArg>({
                query: (queryArg) => ({
                    url: `/users/${queryArg.userId}/settings`,
                    params: { companyId: queryArg.companyId },
                }),
                providesTags: ['User'],
            }),
            userUpdateUserSettings: build.mutation<UserUpdateUserSettingsApiResponse, UserUpdateUserSettingsApiArg>({
                query: (queryArg) => ({
                    url: `/users/${queryArg.userId}/settings`,
                    method: 'PUT',
                    body: queryArg.body,
                    params: { companyId: queryArg.companyId },
                }),
                invalidatesTags: ['User'],
            }),
            userLoggedInUserIsCompanyMember: build.query<
                UserLoggedInUserIsCompanyMemberApiResponse,
                UserLoggedInUserIsCompanyMemberApiArg
            >({
                query: () => ({ url: `/users/iscompanymember` }),
                providesTags: ['User'],
            }),
            userGetPoolsForSelectedCompany: build.query<
                UserGetPoolsForSelectedCompanyApiResponse,
                UserGetPoolsForSelectedCompanyApiArg
            >({
                query: (queryArg) => ({
                    url: `/users/${queryArg.userId}/pools`,
                    params: { companyId: queryArg.companyId },
                }),
                providesTags: ['User'],
            }),
            userGetForUserByNfcAccessWithNfcAccessInfo: build.query<
                UserGetForUserByNfcAccessWithNfcAccessInfoApiResponse,
                UserGetForUserByNfcAccessWithNfcAccessInfoApiArg
            >({
                query: (queryArg) => ({ url: `/users/${queryArg.userId}/chargepoints/${queryArg.companyId}` }),
                providesTags: ['User'],
            }),
            userGetOwnedChargePoints: build.query<UserGetOwnedChargePointsApiResponse, UserGetOwnedChargePointsApiArg>({
                query: (queryArg) => ({ url: `/users/chargepoints/owned`, params: { expand: queryArg.expand } }),
                providesTags: ['User'],
            }),
            userGetOwnedChargePointsWithIds: build.query<
                UserGetOwnedChargePointsWithIdsApiResponse,
                UserGetOwnedChargePointsWithIdsApiArg
            >({
                query: (queryArg) => ({
                    url: `/users/chargepoints/owned`,
                    method: 'POST',
                    body: queryArg.body,
                    params: { expand: queryArg.expand },
                }),
                providesTags: ['User'],
            }),
            userIsUserHasChargePoints: build.query<
                UserIsUserHasChargePointsApiResponse,
                UserIsUserHasChargePointsApiArg
            >({
                query: () => ({ url: `/users/chargepoints/existence` }),
                providesTags: ['User'],
            }),
            userGetUserNotifications: build.query<UserGetUserNotificationsApiResponse, UserGetUserNotificationsApiArg>({
                query: (queryArg) => ({ url: `/users/notifications`, params: { filter: queryArg.filter } }),
                providesTags: ['User'],
            }),
            userGetOwnNfcTags: build.query<UserGetOwnNfcTagsApiResponse, UserGetOwnNfcTagsApiArg>({
                query: () => ({ url: `/users/nfctags/own` }),
                providesTags: ['User'],
            }),
            userUpdateOwnNfcTags: build.mutation<UserUpdateOwnNfcTagsApiResponse, UserUpdateOwnNfcTagsApiArg>({
                query: (queryArg) => ({ url: `/users/nfctags/own`, method: 'PUT', body: queryArg.body }),
                invalidatesTags: ['User'],
            }),
            userUpdateUserGreenChargingSetting: build.mutation<
                UserUpdateUserGreenChargingSettingApiResponse,
                UserUpdateUserGreenChargingSettingApiArg
            >({
                query: (queryArg) => ({
                    url: `/users/${queryArg.userId}/settings/greencharging`,
                    method: 'PUT',
                    params: { companyId: queryArg.companyId },
                }),
                invalidatesTags: ['User'],
            }),
            userRemoveChargePointAccess: build.mutation<
                UserRemoveChargePointAccessApiResponse,
                UserRemoveChargePointAccessApiArg
            >({
                query: (queryArg) => ({
                    url: `/users/chargepoints/own/unregister/${queryArg.chargePointId}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['User'],
            }),
            userGetLatestRejectedTag: build.query<UserGetLatestRejectedTagApiResponse, UserGetLatestRejectedTagApiArg>({
                query: () => ({ url: `/users/nfctags/rejected` }),
                providesTags: ['User'],
            }),
            userSetAllNotificationsToReadForUser: build.mutation<
                UserSetAllNotificationsToReadForUserApiResponse,
                UserSetAllNotificationsToReadForUserApiArg
            >({
                query: () => ({ url: `/users/notifications/read`, method: 'POST' }),
                invalidatesTags: ['User'],
            }),
            userSetNotificationToReadForUser: build.mutation<
                UserSetNotificationToReadForUserApiResponse,
                UserSetNotificationToReadForUserApiArg
            >({
                query: (queryArg) => ({ url: `/users/notifications/${queryArg.notificationId}/read`, method: 'POST' }),
                invalidatesTags: ['User'],
            }),
            userAcceptTerms: build.mutation<UserAcceptTermsApiResponse, UserAcceptTermsApiArg>({
                query: () => ({ url: `/users/acceptterms`, method: 'PUT' }),
                invalidatesTags: ['User'],
            }),
            userIsFavourite: build.query<UserIsFavouriteApiResponse, UserIsFavouriteApiArg>({
                query: (queryArg) => ({ url: `/users/favourites/${queryArg.chargePointId}` }),
                providesTags: ['User'],
            }),
            userAddFavourite: build.mutation<UserAddFavouriteApiResponse, UserAddFavouriteApiArg>({
                query: (queryArg) => ({ url: `/users/favourites/${queryArg.chargePointId}`, method: 'POST' }),
                invalidatesTags: ['User'],
            }),
            userRemoveFavourite: build.mutation<UserRemoveFavouriteApiResponse, UserRemoveFavouriteApiArg>({
                query: (queryArg) => ({ url: `/users/favourites/${queryArg.chargePointId}`, method: 'DELETE' }),
                invalidatesTags: ['User'],
            }),
            userGetFavourites: build.query<UserGetFavouritesApiResponse, UserGetFavouritesApiArg>({
                query: () => ({ url: `/users/favourites` }),
                providesTags: ['User'],
            }),
            userActivateUser: build.mutation<UserActivateUserApiResponse, UserActivateUserApiArg>({
                query: (queryArg) => ({ url: `/users/${queryArg.userId}/activate`, method: 'PUT' }),
                invalidatesTags: ['User'],
            }),
            userGetPartnerCandidate: build.query<UserGetPartnerCandidateApiResponse, UserGetPartnerCandidateApiArg>({
                query: () => ({ url: `/users/partner/candidate` }),
                providesTags: ['User'],
            }),
        }),
        overrideExisting: false,
    })
export { injectedRtkApi as enhancedApi }
export type AlarmGetAllApiResponse = /** status 200  */ Alarm[]
export type AlarmGetAllApiArg = {
    offset?: number
    noOfItems?: number
    expand?: string | null
}
export type ApiCustomerGetHashedApiKeyApiResponse = /** status 200  */ string
export type ApiCustomerGetHashedApiKeyApiArg = void
export type ApiCustomerGetAllApiResponse = /** status 200  */ ApiCustomer[]
export type ApiCustomerGetAllApiArg = {
    enabled?: boolean | null
    callbackEnabled?: boolean | null
}
export type ApiCustomerCreateApiResponse = /** status 200  */ void | /** status 201  */ void
export type ApiCustomerCreateApiArg = {
    apiCustomer: ApiCustomer
}
export type ApiCustomerUpdateApiResponse = /** status 200  */ void
export type ApiCustomerUpdateApiArg = {
    apiCustomer: ApiCustomer
}
export type ApiCustomerDeleteApiResponse = /** status 200  */ void
export type ApiCustomerDeleteApiArg = {
    apiCustomerId: number
}
export type ApiCustomerGetChargePointsApiResponse = /** status 200  */ string[]
export type ApiCustomerGetChargePointsApiArg = {
    apiCustomerId: number
}
export type ArticleGetFilteredArticlesApiResponse = /** status 200  */ ProductConfigurationTemplate[]
export type ArticleGetFilteredArticlesApiArg = {
    productId?: number | null
}
export type AuthLoginApiResponse = unknown
export type AuthLoginApiArg = {
    userCredentials: UserCredentials
}
export type AuthRegisterApiResponse = /** status 201  */ ChargePointUser
export type AuthRegisterApiArg = {
    hostUrl?: string | null
    chargePointUser: ChargePointUser
}
export type AuthVerifyEmailApiResponse = unknown
export type AuthVerifyEmailApiArg = {
    token: string
}
export type AuthAcceptInvitationApiResponse = unknown
export type AuthAcceptInvitationApiArg = {
    token: string
}
export type AuthRequestPasswordResetApiResponse = unknown
export type AuthRequestPasswordResetApiArg = {
    hostUrl?: string | null
    userCredentials: UserCredentials
}
export type AuthRequestPasswordApiResponse = unknown
export type AuthRequestPasswordApiArg = {
    token: string
    userCredentials: UserCredentials
}
export type AuthRegisterPartnerCandidateApiResponse = unknown
export type AuthRegisterPartnerCandidateApiArg = {
    userId: string
    partner: Partner
}
export type ChargePointUpdateApiResponse = /** status 200  */ void
export type ChargePointUpdateApiArg = {
    chargePoint: ChargePoint
}
export type ChargePointUpdateChargerInformationApiResponse = /** status 200  */ void | /** status 204  */ void
export type ChargePointUpdateChargerInformationApiArg = {
    chargePointId: string
    chargerInformation: ChargerInformation
}
export type ChargePointCreateFromTemplateApiResponse = /** status 200  */ ChargePoint | /** status 201  */ ChargePoint
export type ChargePointCreateFromTemplateApiArg = {
    monitorArticleId: string
    processorId?: string | null
}
export type ChargePointGetChargePointsApiResponse = /** status 200  */ PagedResultOfChargePoint
export type ChargePointGetChargePointsApiArg = {
    id?: string | null
    name?: string | null
    ocppOnly?: boolean | null
    productId?: number | null
    partnerId?: number | null
    companyId?: string | null
    companyName?: string | null
    installedFirmwareVersion?: string | null
    firmwareType?: FirmwareType | null
    imsiNumber?: string | null
    ownerName?: string | null
    ownerEmail?: string | null
    ownerMobile?: string | null
    status?: ChargePointStatus | null
    errorCode?: string | null
    page?: number | null
    noOfItems?: number | null
    column?: string | null
    sortOrder?: SortOrder
    expand?: string | null
}
export type ChargePointGetApiResponse = /** status 200  */ ChargePoint
export type ChargePointGetApiArg = {
    chargePointId: string
    wifiPassword?: string | null
    expand?: string | null
}
export type ChargePointGetPartnerApiResponse = /** status 200  */ Partner
export type ChargePointGetPartnerApiArg = {
    chargePointId: string
}
export type ChargePointSaveCalibrationApiResponse = /** status 200  */ void
export type ChargePointSaveCalibrationApiArg = {
    chargePointCalibration: ChargePointCalibration
}
export type ChargePointSetupCallbackApiResponse = /** status 200  */ void
export type ChargePointSetupCallbackApiArg = {
    chargePointId: string
    setupCallback: SetupCallback
}
export type ChargePointGetCalibrationApiResponse = /** status 200  */ ChargePointCalibration
export type ChargePointGetCalibrationApiArg = {
    chargePointId: string
}
export type ChargePointGetNewestApiResponse = /** status 200  */ ChargePoint
export type ChargePointGetNewestApiArg = {
    productId?: number | null
}
export type ChargePointGetChargingSessionsWithCountApiResponse = /** status 200  */ ChargingSessionsWithCount
export type ChargePointGetChargingSessionsWithCountApiArg = {
    filterZero?: boolean | null
    chargePointId: string
    offset?: number
    noOfItems?: number
}
export type ChargePointDeleteChargingSessionsApiResponse = /** status 200  */ void | /** status 204  */ void
export type ChargePointDeleteChargingSessionsApiArg = {
    chargePointId: string
    startTime?: string | null
    endTime?: string | null
}
export type ChargePointAddToPoolApiResponse = /** status 200  */ void
export type ChargePointAddToPoolApiArg = {
    chargePointId: string
    poolId: string
}
export type ChargePointSetOwnerApiResponse = /** status 200  */ void
export type ChargePointSetOwnerApiArg = {
    chargePointId: string
    userId: string
    name: string
    wifiPassword: string
}
export type ChargePointRemoveOwnerApiResponse = /** status 200  */ void
export type ChargePointRemoveOwnerApiArg = {
    chargePointId: string
}
export type ChargePointGetManualApiResponse = /** status 200  */ Blob
export type ChargePointGetManualApiArg = {
    chargePointId: string
}
export type ChargePointRebootApiResponse = /** status 200  */ void
export type ChargePointRebootApiArg = {
    chargePointId: string
}
export type ChargePointGetRemoteStartTagsApiResponse = /** status 200  */ NfcTag[]
export type ChargePointGetRemoteStartTagsApiArg = {
    chargePointId: string
    connectorId: number
}
export type ChargePointRemoteStartApiResponse = /** status 200  */ void
export type ChargePointRemoteStartApiArg = {
    chargePointId: string
    connectorId: number
    rfidTag?: string | null
}
export type ChargePointRemoteStopApiResponse = /** status 200  */ void
export type ChargePointRemoteStopApiArg = {
    chargePointId: string
    connectorId: number
    userId?: string | null
}
export type ChargePointOverrideScheduleForOngoingChargingSessionApiResponse = /** status 200  */ void
export type ChargePointOverrideScheduleForOngoingChargingSessionApiArg = {
    chargePointId: string
    connectorId: number
}
export type ChargePointGetScheduleOverrideStatusApiResponse = /** status 200  */ ConnectorScheduleOverrideStatus[]
export type ChargePointGetScheduleOverrideStatusApiArg = {
    chargePointId: string
}
export type ChargePointStartTestApiResponse = /** status 200  */ void
export type ChargePointStartTestApiArg = {
    chargePointId: string
}
export type ChargePointSetProductionConfigurationApiResponse = /** status 200  */ void
export type ChargePointSetProductionConfigurationApiArg = {
    chargePointId: string
    chargePoint: ChargePoint
}
export type ChargePointChangeProcessorIdApiResponse = /** status 200  */ void
export type ChargePointChangeProcessorIdApiArg = {
    chargePointId: string
    changeProcessorId: ChangeProcessorId
}
export type ChargePointGetMaintenanceLogsApiResponse = /** status 200  */ MaintenanceLog[]
export type ChargePointGetMaintenanceLogsApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
    accessLevel?: MaintenanceLogAccessLevel
}
export type ChargePointAddMaintenanceLogApiResponse = /** status 200  */ void
export type ChargePointAddMaintenanceLogApiArg = {
    chargePointId: string
    maintenanceLog: MaintenanceLog
}
export type ChargePointGetAlarmsApiResponse = /** status 200  */ Alarm[]
export type ChargePointGetAlarmsApiArg = {
    chargePointId: string
    showHidden?: boolean
    expand?: string | null
}
export type ChargePointAcknowledgeAlarmApiResponse = /** status 200  */ void
export type ChargePointAcknowledgeAlarmApiArg = {
    chargePointId: string
    alarmId: number
}
export type ChargePointDeleteRejectedTagApiResponse = /** status 200  */ void
export type ChargePointDeleteRejectedTagApiArg = {
    chargePointId: string
    nfcTagId: string
}
export type ChargePointAddChargePointProductionInfoApiResponse = /** status 200  */ void
export type ChargePointAddChargePointProductionInfoApiArg = {
    chargePointId: string
    chargePointProductionInfo: ChargePointProductionInfo
}
export type ChargePointGetLatestChargePointProductionInfoApiResponse = /** status 200  */ ChargePointProductionInfo[]
export type ChargePointGetLatestChargePointProductionInfoApiArg = {
    chargePointId: string
}
export type ChargePointGetChargePointProductionInfoApiResponse = /** status 200  */ ChargePointProductionInfo[]
export type ChargePointGetChargePointProductionInfoApiArg = {
    chargePointId: string
}
export type ChargePointDisableOcppApiResponse = /** status 200  */ void
export type ChargePointDisableOcppApiArg = {
    chargePointId: string
    forceUpdate?: boolean
}
export type ChargePointEnableOcppApiResponse = /** status 200  */ void
export type ChargePointEnableOcppApiArg = {
    chargePointId: string
    forceUpdate?: boolean
}
export type ChargePointChangeOcppServiceApiResponse = /** status 200  */ void
export type ChargePointChangeOcppServiceApiArg = {
    chargePointId: string
    ocppProxyServiceId: number
}
export type ChargePointSetOcppServiceApiResponse = /** status 200  */ void
export type ChargePointSetOcppServiceApiArg = {
    chargePointId: string
    ocppProxyServiceId: number
}
export type ChargePointUpdateFirmwareApiResponse = /** status 200  */ void
export type ChargePointUpdateFirmwareApiArg = {
    updateChargerFirmwareOnSeveralModel: UpdateChargerFirmwareOnSeveralModel
}
export type ChargePointUpdateFirmware2ApiResponse = /** status 200  */ void
export type ChargePointUpdateFirmware2ApiArg = {
    chargePointId: string
    updateChargerFirmwareModel: UpdateChargerFirmwareModel
}
export type ChargePointGetOcppCommunicationLogsApiResponse = /** status 200  */ OcppCommunicationLog[]
export type ChargePointGetOcppCommunicationLogsApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
    actions?: string | null
}
export type ChargePointGetOcppRelayLogsApiResponse = /** status 200  */ OcppCommunicationLog[]
export type ChargePointGetOcppRelayLogsApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
    actions?: string | null
}
export type ChargePointResetApiResponse = /** status 200  */ void
export type ChargePointResetApiArg = {
    chargePointId: string
}
export type ChargePointResetAvailableFirmwareVersionApiResponse = /** status 200  */ void
export type ChargePointResetAvailableFirmwareVersionApiArg = {
    chargePointId: string
}
export type ChargePointGetStatusIntervalsApiResponse = /** status 200  */ StatusInterval[]
export type ChargePointGetStatusIntervalsApiArg = void
export type ChargePointUpdateChargePointUserSettingsApiResponse = /** status 200  */ void
export type ChargePointUpdateChargePointUserSettingsApiArg = {
    chargePointId: string
    name?: string | null
    userCurrentConnector1?: number | null
    userCurrentConnector2?: number | null
}
export type ChargePointClearCacheApiResponse = /** status 200  */ void
export type ChargePointClearCacheApiArg = {
    chargePointId: string
}
export type ChargePointUpdateConnectorAvailabilityApiResponse = /** status 200  */ void
export type ChargePointUpdateConnectorAvailabilityApiArg = {
    /** The charge point id */
    chargePointId: string
    /** The connector id */
    connectorId: number
    /** Valid values for OCPP:
    - Operative
    - Inoperative
    
    Valid values for CAPI:
    - On
    - Off
    - Schedule */
    availability: string
}
export type ChargePointDataGetLatestChargePointDataApiResponse = /** status 200  */ ChargePointData
export type ChargePointDataGetLatestChargePointDataApiArg = {
    chargePointId: string
    dataTypeId?: number | null
}
export type ChargePointDataGetChargePointDataWithFilterApiResponse = /** status 200  */ ChargePointData[]
export type ChargePointDataGetChargePointDataWithFilterApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
    dataLogFilter: DataLogFilter
}
export type ChargePointDataGetChargePointDataApiResponse = /** status 200  */ ChargePointData[]
export type ChargePointDataGetChargePointDataApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
}
export type ChargingSessionGetConsumptionForSessionApiResponse = /** status 200  */ Consumption
export type ChargingSessionGetConsumptionForSessionApiArg = {
    id: number
}
export type ChargingSessionGetChargingSessionsForCurrentUserApiResponse = /** status 200  */ ChargingSession[]
export type ChargingSessionGetChargingSessionsForCurrentUserApiArg = {
    offset?: number
    noOfItems?: number
}
export type ChargingSessionGetChargingSessionsForLoggedInUserApiResponse = /** status 200  */ ChargingSession[]
export type ChargingSessionGetChargingSessionsForLoggedInUserApiArg = {
    userId: string
    offset?: number
    noOfItems?: number
}
export type ChargingSessionDeleteChargingSessionApiResponse = /** status 200  */ void | /** status 204  */ void
export type ChargingSessionDeleteChargingSessionApiArg = {
    id: number
}
export type CircuitBoardCreateCircuitBoardApiResponse = /** status 200  */ CircuitBoard
export type CircuitBoardCreateCircuitBoardApiArg = {
    circuitBoard: CircuitBoard
}
export type CircuitBoardGetMacAddressApiResponse = /** status 200  */ number
export type CircuitBoardGetMacAddressApiArg = {
    chargePointId: string
    body: string
}
export type CircuitBoardSetExternalOcppServerApiResponse = /** status 200  */ void
export type CircuitBoardSetExternalOcppServerApiArg = {
    chargePointId: string
    externalServerId: number
}
export type CompanyGetAllOrFilteredApiResponse = /** status 200  */ Company[]
export type CompanyGetAllOrFilteredApiArg = {
    isPartnerRestricted?: boolean
    numberOfItems?: number | null
    name?: string | null
    partnerName?: string | null
    partnerId?: number | null
}
export type CompanyCreateCompanyApiResponse = /** status 200  */ Company | /** status 201  */ Company
export type CompanyCreateCompanyApiArg = {
    company: Company
}
export type CompanyUpdateCompanyApiResponse = /** status 200  */ void
export type CompanyUpdateCompanyApiArg = {
    company: Company
}
export type CompanyGetCompanyByIdApiResponse = /** status 200  */ Company
export type CompanyGetCompanyByIdApiArg = {
    companyId: string
}
export type CompanyDeleteCompanyApiResponse = /** status 200  */ void | /** status 204  */ void
export type CompanyDeleteCompanyApiArg = {
    companyId: string
}
export type CompanyGetAdministratedCompaniesApiResponse = /** status 200  */ CompanyPreview[]
export type CompanyGetAdministratedCompaniesApiArg = void
export type CompanyGetUsersApiResponse = /** status 200  */ ChargePointUser[]
export type CompanyGetUsersApiArg = {
    companyId: string
}
export type CompanyCreateUserApiResponse = /** status 200  */ ChargePointUser[]
export type CompanyCreateUserApiArg = {
    companyId: string
    chargePointUser: ChargePointUser
}
export type CompanyUpdateUserApiResponse = /** status 200  */ void
export type CompanyUpdateUserApiArg = {
    companyId: string
    chargePointUser: ChargePointUser
}
export type CompanyGetChargePointsPagedApiResponse = /** status 200  */ PagedResultOfChargePoint
export type CompanyGetChargePointsPagedApiArg = {
    companyId: string
    id?: string | null
    name?: string | null
    ocppOnly?: boolean | null
    productId?: number | null
    partnerId?: number | null
    CompanyId?: string | null
    companyName?: string | null
    installedFirmwareVersion?: string | null
    firmwareType?: FirmwareType | null
    imsiNumber?: string | null
    ownerName?: string | null
    ownerEmail?: string | null
    ownerMobile?: string | null
    status?: ChargePointStatus | null
    errorCode?: string | null
    page?: number | null
    noOfItems?: number | null
    column?: string | null
    sortOrder?: SortOrder
    expand?: string | null
}
export type CompanyGetPartnerApiResponse = /** status 200  */ Partner
export type CompanyGetPartnerApiArg = {
    companyId: string
}
export type CompanyGetReportDeliverySchedulesApiResponse = /** status 200  */ ReportDeliverySchedule[]
export type CompanyGetReportDeliverySchedulesApiArg = {
    companyId: string
}
export type CompanyCreateReportDeliveryScheduleApiResponse = /** status 200  */ void
export type CompanyCreateReportDeliveryScheduleApiArg = {
    companyId: string
    reportDeliverySchedule: ReportDeliverySchedule
}
export type CompanyUpdateReportDeliveryScheduleApiResponse = /** status 200  */ void
export type CompanyUpdateReportDeliveryScheduleApiArg = {
    companyId: string
    reportDeliverySchedule: ReportDeliverySchedule
}
export type CompanyDeleteReportDeliveryScheduleApiResponse = /** status 200  */ void | /** status 204  */ void
export type CompanyDeleteReportDeliveryScheduleApiArg = {
    companyId: string
    reportDeliveryScheduleId: number
}
export type CompanyValidateImportChargepointsApiResponse = /** status 200  */ ChargePointImport[]
export type CompanyValidateImportChargepointsApiArg = {
    companyId: string
}
export type CompanyImportChargepointsApiResponse = /** status 200  */ void
export type CompanyImportChargepointsApiArg = {
    companyId: string
    body: ChargePointImport[]
}
export type CompanyCreateImportStringApiResponse = /** status 200  */ string
export type CompanyCreateImportStringApiArg = {
    companyId: string
}
export type CompanyGetPoolsApiResponse = /** status 200  */ Pool[]
export type CompanyGetPoolsApiArg = {
    companyId: string
}
export type CompanyGetChargingSessionsByRangeApiResponse = /** status 200  */ ChargingSession[]
export type CompanyGetChargingSessionsByRangeApiArg = {
    companyId: string
    startTime?: string | null
    endTime?: string | null
}
export type CompanyGetChargingSessionsApiResponse = /** status 200  */ IDetailedChargingSession[]
export type CompanyGetChargingSessionsApiArg = {
    companyId: string
    offset?: number
    noOfItems?: number
}
export type CompanyDeleteChargingSessionsApiResponse = /** status 200  */ void | /** status 204  */ void
export type CompanyDeleteChargingSessionsApiArg = {
    companyId: string
    startTime?: string | null
    endTime?: string | null
}
export type CompanyGetNfcTagsApiResponse = /** status 200  */ NfcTag[]
export type CompanyGetNfcTagsApiArg = {
    companyId: string
}
export type CompanyGetLoadbalancerGroupsPagedApiResponse = /** status 200  */ PagedResultOfLoadBalancerGroup
export type CompanyGetLoadbalancerGroupsPagedApiArg = {
    companyId: string
    id?: string | null
    name?: string | null
    companyName?: string | null
    page?: number | null
    noOfItems?: number | null
}
export type CompanyAddRoleToUserApiResponse = /** status 200  */ void | /** status 201  */ void
export type CompanyAddRoleToUserApiArg = {
    userId: string
    companyId: string
    role: Role
}
export type CompanyUpdateRoleToUserApiResponse = /** status 200  */ void
export type CompanyUpdateRoleToUserApiArg = {
    userId: string
    companyId: string
    role: Role
}
export type CompanyAddChargePointToCompanyApiResponse = /** status 200  */ void
export type CompanyAddChargePointToCompanyApiArg = {
    companyId: string
    chargePointCredentials: ChargePointCredentials
}
export type CompanyRemoveChargePointApiResponse = /** status 200  */ void
export type CompanyRemoveChargePointApiArg = {
    companyId: string
    chargePointId: string
}
export type CompanyRemoveUserApiResponse = /** status 200  */ void
export type CompanyRemoveUserApiArg = {
    companyId: string
    userId: string
}
export type CompanyGetChargePointsWithNfcInfoApiResponse = /** status 200  */ ChargePoint[]
export type CompanyGetChargePointsWithNfcInfoApiArg = {
    companyId: string
    userId: string
}
export type CompanySetChargePointsNfcInfoApiResponse = /** status 200  */ void
export type CompanySetChargePointsNfcInfoApiArg = {
    companyId: string
    userId: string
    body: ChargePointNfcInfo[]
}
export type CompanyGetPoolsWithNfcInfoApiResponse = /** status 200  */ Pool[]
export type CompanyGetPoolsWithNfcInfoApiArg = {
    companyId: string
    userId: string
}
export type CompanySetPoolsNfcInfoApiResponse = /** status 200  */ void
export type CompanySetPoolsNfcInfoApiArg = {
    companyId: string
    userId: string
    body: Pool[]
}
export type CompanyGetChargePointsWithNfcInfoForTagApiResponse = /** status 200  */ ChargePoint[]
export type CompanyGetChargePointsWithNfcInfoForTagApiArg = {
    companyId: string
    nfcTagId: string
}
export type CompanySetChargePointsNfcInfoForTagApiResponse = /** status 200  */ void
export type CompanySetChargePointsNfcInfoForTagApiArg = {
    companyId: string
    nfcTagId: string
    body: ChargePointNfcInfo[]
}
export type CompanyGetPoolsWithNfcInfoForTagApiResponse = /** status 200  */ Pool[]
export type CompanyGetPoolsWithNfcInfoForTagApiArg = {
    companyId: string
    nfcTagId: string
}
export type CompanySetPoolsNfcInfoForTagApiResponse = /** status 200  */ void
export type CompanySetPoolsNfcInfoForTagApiArg = {
    companyId: string
    nfcTagId: string
    body: Pool[]
}
export type CompanyGetNfcTagsForUserApiResponse = /** status 200  */ NfcTag[]
export type CompanyGetNfcTagsForUserApiArg = {
    companyId: string
    userId: string
}
export type CompanyGetChargePointsForUserApiResponse = /** status 200  */ ChargePoint[]
export type CompanyGetChargePointsForUserApiArg = {
    companyId: string
    userId: string
}
export type CompanyInviteExistingUserToCompanyApiResponse = /** status 200  */ void
export type CompanyInviteExistingUserToCompanyApiArg = {
    companyId: string
    role: string
    origin?: string | null
    chargePointUser: ChargePointUser
}
export type CompanyAddMeasurementDeviceToCompanyApiResponse = /** status 200  */ void
export type CompanyAddMeasurementDeviceToCompanyApiArg = {
    companyId: string
    measurementDeviceCredentials: MeasurementDeviceCredentials
}
export type CompanyGetDeviceByCompanyIdApiResponse = /** status 200  */ MeasurementDevicesGetModel[]
export type CompanyGetDeviceByCompanyIdApiArg = {
    companyId: string
}
export type CompanyRemoveMeasurementDeviceApiResponse = /** status 200  */ void | /** status 204  */ void
export type CompanyRemoveMeasurementDeviceApiArg = {
    companyId: string
    measurementDeviceId: number
}
export type CompanyTransferGetPartnerTransferKeyApiResponse = /** status 200  */ string
export type CompanyTransferGetPartnerTransferKeyApiArg = {
    partnerId: number
}
export type CompanyTransferGetPartnerApiResponse = /** status 200  */ Partner
export type CompanyTransferGetPartnerApiArg = {
    companyId: string
    transferKey: string
}
export type CompanyTransferGetRequestForCompanyApiResponse = /** status 200  */ CompanyTransferRequest
export type CompanyTransferGetRequestForCompanyApiArg = {
    companyId: string
}
export type CompanyTransferGetRequestsForPartnerApiResponse = /** status 200  */ CompanyTransferRequest[]
export type CompanyTransferGetRequestsForPartnerApiArg = {
    partnerId: number
}
export type CompanyTransferGetRequestsApiResponse = /** status 200  */ CompanyTransferRequest[]
export type CompanyTransferGetRequestsApiArg = void
export type CompanyTransferRequestTransferApiResponse = unknown
export type CompanyTransferRequestTransferApiArg = {
    transferKey: string
    companyId: string
    validToInUtc?: string | null
}
export type CompanyTransferAcceptRequestApiResponse = /** status 200  */ Company[]
export type CompanyTransferAcceptRequestApiArg = {
    partnerId: number
    companyId: string
}
export type CompanyTransferRejectRequestApiResponse = /** status 200  */ Company[]
export type CompanyTransferRejectRequestApiArg = {
    partnerId: number
    companyId: string
}
export type CompanyTransferBatchTransferApiResponse = unknown
export type CompanyTransferBatchTransferApiArg = {
    partnerId: number
    body: string[]
}
export type ComputedNfcAccessGetAccessesForChargePointApiResponse = /** status 200  */ ComputedNfcAccess[]
export type ComputedNfcAccessGetAccessesForChargePointApiArg = {
    chargePointId: string
}
export type CountrySettingsGetAllCountrySettingsApiResponse = /** status 200  */ CountrySettings[]
export type CountrySettingsGetAllCountrySettingsApiArg = void
export type ExternalServerGetAllApiResponse = /** status 200  */ ExternalServer[]
export type ExternalServerGetAllApiArg = void
export type ExternalServerCreateApiResponse = /** status 200  */ ExternalServer
export type ExternalServerCreateApiArg = {
    externalServer: ExternalServer
}
export type ExternalServerGetByChargePointIdApiResponse = /** status 200  */ ExternalServer
export type ExternalServerGetByChargePointIdApiArg = {
    chargePointId: string
}
export type ExternalServerGetChargePointsApiResponse = /** status 200  */ string[]
export type ExternalServerGetChargePointsApiArg = {
    externalServerId: number
}
export type ExternalServerGetPartnersApiResponse = /** status 200  */ Partner[]
export type ExternalServerGetPartnersApiArg = {
    externalServerId: number
}
export type ExternalServerUpdatePartnerConnectionsApiResponse = /** status 200  */ number
export type ExternalServerUpdatePartnerConnectionsApiArg = {
    externalServerId: number
    body: number[]
}
export type ExternalServerDeleteApiResponse = unknown
export type ExternalServerDeleteApiArg = {
    externalServerId: number
}
export type FirmwareGetFirmwaresByFilterApiResponse = /** status 200  */ PagedResultOfFirmwareGeneralModel
export type FirmwareGetFirmwaresByFilterApiArg = {
    productId: number
    ProductId?: number | null
    firmwareType?: FirmwareType | null
    isActive?: boolean | null
    ocppEnabled?: boolean | null
    firmwareStatuses?: FirmwareStatus[] | null
    page?: number | null
    noOfItems?: number | null
}
export type FirmwareUploadFirmwareApiResponse = /** status 200  */ void | /** status 201  */ void
export type FirmwareUploadFirmwareApiArg = {
    productId: number
    body: {
        FirmwareServerLocationId?: number
        ProductId?: number
        FirmwareVersion?: string | null
        FirmwareType?: string | null
        Description?: string | null
        OcppEnabledFirmware?: boolean
        File?: Blob | null
    }
}
export type FirmwareUpdateFirmwareApiResponse = /** status 200  */ void
export type FirmwareUpdateFirmwareApiArg = {
    firmwareGeneralModel: FirmwareGeneralModel
}
export type FirmwareDeleteFirmwareApiResponse = /** status 200  */ void | /** status 204  */ void
export type FirmwareDeleteFirmwareApiArg = {
    productId: number
    firmwareId: number
}
export type FirmwareSetActiveFirmwareApiResponse = /** status 200  */ void
export type FirmwareSetActiveFirmwareApiArg = {
    productId: number
    firmwareId: number
}
export type FirmwareGetActiveFirmwareApiResponse = /** status 200  */ FirmwareGeneralModel
export type FirmwareGetActiveFirmwareApiArg = {
    productId: number
}
export type GreenChargingGetLoadBalancerGroupApiResponse = /** status 200  */ GreenChargingGroup
export type GreenChargingGetLoadBalancerGroupApiArg = {
    loadBalancerGroupId: number
}
export type GreenChargingChargePointStatusApiResponse = /** status 200  */ GreenChargingChargePointStatus
export type GreenChargingChargePointStatusApiArg = {
    chargePointId: string
}
export type GreenChargingUpdateLoadBalancerGroupApiResponse = /** status 200  */ void
export type GreenChargingUpdateLoadBalancerGroupApiArg = {
    updateGreenChargingGroup: UpdateGreenChargingGroup
}
export type InstallationSetupApiResponse = /** status 200  */ void
export type InstallationSetupApiArg = {
    setupModel: SetupModel
}
export type LoadBalancerGroupGetAllPagedApiResponse = /** status 200  */ PagedResultOfLoadBalancerGroup
export type LoadBalancerGroupGetAllPagedApiArg = {
    id?: string | null
    name?: string | null
    companyName?: string | null
    page?: number | null
    noOfItems?: number | null
}
export type LoadBalancerGroupCreateApiResponse = /** status 200  */ LoadBalancerGroup
export type LoadBalancerGroupCreateApiArg = {
    loadBalancerGroup: LoadBalancerGroup
}
export type LoadBalancerGroupUpdateApiResponse = /** status 200  */ LoadBalancerGroup
export type LoadBalancerGroupUpdateApiArg = {
    loadBalancerGroup: LoadBalancerGroup
}
export type LoadBalancerGroupDeleteApiResponse = /** status 200  */ void
export type LoadBalancerGroupDeleteApiArg = {
    id: number
}
export type LoadBalancerGroupGetByIdApiResponse = /** status 200  */ LoadBalancerGroup
export type LoadBalancerGroupGetByIdApiArg = {
    id: number
}
export type LoadBalancerGroupGetChargePointsForGroupApiResponse = /** status 200  */ ChargePoint[]
export type LoadBalancerGroupGetChargePointsForGroupApiArg = {
    id: number
    expand?: string | null
}
export type LoadBalancerGroupGetCurrentLimitSchemeApiResponse = /** status 200  */ CurrentLimitScheme
export type LoadBalancerGroupGetCurrentLimitSchemeApiArg = {
    id: number
    currentLimitSchemeId: number
}
export type LoadBalancerGroupInsertOrUpdateCurrentLimitSchemeApiResponse = /** status 200  */ void
export type LoadBalancerGroupInsertOrUpdateCurrentLimitSchemeApiArg = {
    id: number
    currentLimitScheme: CurrentLimitScheme
}
export type LoadBalancerGroupDisableGroupApiResponse = /** status 200  */ void
export type LoadBalancerGroupDisableGroupApiArg = {
    id: number
}
export type LoadBalancerGroupEnableGroupApiResponse = /** status 200  */ void
export type LoadBalancerGroupEnableGroupApiArg = {
    id: number
}
export type LoadBalancerGroupValidateChargePointImportsApiResponse = /** status 200  */ ChargePointImport[]
export type LoadBalancerGroupValidateChargePointImportsApiArg = {
    id: number
    companyId?: string | null
}
export type LoadBalancerGroupImportChargePointsApiResponse = /** status 200  */ void
export type LoadBalancerGroupImportChargePointsApiArg = {
    id: number
    body: ChargePointImport[]
}
export type LoadBalancerGroupImportChargePointsV2ApiResponse = /** status 200  */ void
export type LoadBalancerGroupImportChargePointsV2ApiArg = {
    id: number
    loadBalancerChargePointsImportModel: LoadBalancerChargePointsImportModel
}
export type LoadBalancerGroupDeleteChargePointFromGroupApiResponse = /** status 200  */ void
export type LoadBalancerGroupDeleteChargePointFromGroupApiArg = {
    id: number
    chargePointId: string
}
export type LoadBalancerGroupGetSnapshotByDeviceIdApiResponse = /** status 200  */ LoadBalancerSnapshot[]
export type LoadBalancerGroupGetSnapshotByDeviceIdApiArg = {
    measurementDeviceId: number
}
export type LogCreateLogApiResponse = /** status 200  */ void
export type LogCreateLogApiArg = {
    applicationLog: ApplicationLog
}
export type LogGetFilteredLogsApiResponse = /** status 200  */ ApplicationLog[]
export type LogGetFilteredLogsApiArg = {
    chargePointUserId?: string | null
    logLevel?: string | null
    origin?: string | null
    chargePointId?: string | null
    start?: string | null
    end?: string | null
    message?: string | null
    offset?: number
    noOfItems?: number
}
export type LogGetLogApiResponse = /** status 200  */ ApplicationLog
export type LogGetLogApiArg = {
    id: number
}
export type LogGetErrorsApiResponse = /** status 200  */ ApplicationLog
export type LogGetErrorsApiArg = {
    noOfItems?: number | null
}
export type LogGetByLatestChargePointExcludingOcpp2ApiResponse = /** status 200  */ ApplicationLog[]
export type LogGetByLatestChargePointExcludingOcpp2ApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
}
export type LogGetByLatestChargePointExcludingOcppApiResponse = /** status 200  */ ApplicationLog[]
export type LogGetByLatestChargePointExcludingOcppApiArg = {
    chargePointId: string
    offset?: number
    noOfItems?: number
    logFilter: LogFilter
}
export type MeasurementDevicesGetFilteredApiResponse = /** status 200  */ MeasurementDevicesGetModel[]
export type MeasurementDevicesGetFilteredApiArg = {
    serialNumber?: string | null
    name?: string | null
    companyName?: string | null
    partnerName?: string | null
    productId?: number | null
    partnerId?: number | null
    companyId?: string | null
    status?: MeasurementDeviceStatus | null
    limit?: number
}
export type MeasurementDevicesCreateApiResponse = /** status 201  */ MeasurementDeviceExtendedGetModel
export type MeasurementDevicesCreateApiArg = {
    createMeasurementDeviceApiModel: CreateMeasurementDeviceApiModel
}
export type MeasurementDevicesGetByIdApiResponse = /** status 200  */ MeasurementDeviceExtendedGetModel
export type MeasurementDevicesGetByIdApiArg = {
    idOrSerialNumber: string
}
export type MeasurementDevicesUpdateApiResponse = /** status 200  */ void
export type MeasurementDevicesUpdateApiArg = {
    id: number
    updateMeasurementDeviceModel: UpdateMeasurementDeviceModel
}
export type MeasurementDevicesGetDevicesByUserIdApiResponse = /** status 200  */ MeasurementDeviceInfoUserGetModel[]
export type MeasurementDevicesGetDevicesByUserIdApiArg = {
    userId: string
}
export type MeasurementDevicesRemoveMeasurementDeviceFromCompanyApiResponse = /** status 204  */ void
export type MeasurementDevicesRemoveMeasurementDeviceFromCompanyApiArg = {
    id: number
}
export type MeasurementDevicesGetLatestMeasurementApiResponse = /** status 200  */ MeasurementLatestModel
export type MeasurementDevicesGetLatestMeasurementApiArg = {
    id: number
}
export type MeasurementDevicesGetMeasurementsForLatest24HoursApiResponse =
    /** status 200  */ MeasurementLatestDayModel[]
export type MeasurementDevicesGetMeasurementsForLatest24HoursApiArg = {
    id: number
}
export type MeasurementDevicesGetValidatedMeasurementDeviceApiResponse = /** status 200  */ MeasurementDeviceGetModel
export type MeasurementDevicesGetValidatedMeasurementDeviceApiArg = {
    measurementDeviceCredentials: MeasurementDeviceCredentials
}
export type MigrationBackupMigrationStateApiResponse = /** status 200  */ void
export type MigrationBackupMigrationStateApiArg = {
    chargePointId: string
    token?: string | null
}
export type MigrationShouldMigrateScheduleApiResponse = /** status 200  */ boolean
export type MigrationShouldMigrateScheduleApiArg = {
    chargePointId: string
    connectorId: number
}
export type NfcTagCreateApiResponse = /** status 200  */ void | /** status 201  */ void
export type NfcTagCreateApiArg = {
    nfcTag: NfcTag
}
export type NfcTagUpdateApiResponse = /** status 200  */ void
export type NfcTagUpdateApiArg = {
    nfcTag: NfcTag
}
export type NfcTagConnectToUserApiResponse = /** status 200  */ void
export type NfcTagConnectToUserApiArg = {
    nfcTagId: string
    userId: string
}
export type NfcTagDisconnectFromUserApiResponse = /** status 200  */ void | /** status 204  */ void
export type NfcTagDisconnectFromUserApiArg = {
    nfcTagId: string
    userId: string
}
export type NfcTagGetAccessedChargePointsInCompanyApiResponse = /** status 200  */ ChargePoint[]
export type NfcTagGetAccessedChargePointsInCompanyApiArg = {
    nfcTagId: string
    companyId: string
}
export type NfcTagGetAccessedPoolsInCompanyApiResponse = /** status 200  */ Pool[]
export type NfcTagGetAccessedPoolsInCompanyApiArg = {
    nfcTagId: string
    companyId: string
}
export type NfcTagDeleteApiResponse = /** status 200  */ void | /** status 204  */ void
export type NfcTagDeleteApiArg = {
    nfcTagId: string
}
export type NfcTagGetRejectedTagsApiResponse = /** status 200  */ PagedResultOfRejectedAccess
export type NfcTagGetRejectedTagsApiArg = {
    chargePointId?: string | null
    companyId?: string | null
    partnerId?: number | null
    from?: string | null
    to?: string | null
    page?: number | null
    noOfItems?: number | null
}
export type NotificationGetAllOrFilteredApiResponse = /** status 200  */ Notification[]
export type NotificationGetAllOrFilteredApiArg = {
    filter?: NotificationFilterType
}
export type NotificationCreateNotificationApiResponse = /** status 200  */ Notification
export type NotificationCreateNotificationApiArg = {
    notification: Notification
}
export type NotificationGetByIdApiResponse = /** status 200  */ Notification
export type NotificationGetByIdApiArg = {
    id: number
}
export type NotificationUpdateNotificationApiResponse = /** status 200  */ void
export type NotificationUpdateNotificationApiArg = {
    id: number
    notification: Notification
}
export type NotificationDeleteNotificationApiResponse = /** status 200  */ void
export type NotificationDeleteNotificationApiArg = {
    id: number
}
export type NotificationPublishNotificationApiResponse = /** status 200  */ void
export type NotificationPublishNotificationApiArg = {
    id: number
}
export type OcppCommandResetApiResponse = /** status 200  */ void
export type OcppCommandResetApiArg = {
    chargePointId: string
    resetReq: ResetReq
}
export type OcppCommandClearCacheApiResponse = /** status 200  */ void
export type OcppCommandClearCacheApiArg = {
    chargePointId: string
}
export type OcppCommandDataTransferApiResponse = /** status 200  */ void
export type OcppCommandDataTransferApiArg = {
    chargePointId: string
    dataTransferReq: DataTransferReq
}
export type OcppCommandGetConfigurationApiResponse = /** status 200  */ void
export type OcppCommandGetConfigurationApiArg = {
    chargePointId: string
    getConfigurationReq: GetConfigurationReq
}
export type OcppCommandGetDiagnosticsApiResponse = /** status 200  */ void
export type OcppCommandGetDiagnosticsApiArg = {
    chargePointId: string
    getDiagnosticsReq: GetDiagnosticsReq
}
export type OcppCommandUnlockConnectorApiResponse = /** status 200  */ void
export type OcppCommandUnlockConnectorApiArg = {
    chargePointId: string
    unlockConnectorReq: UnlockConnectorReq
}
export type OcppCommandChangeAvailabilityApiResponse = /** status 200  */ void
export type OcppCommandChangeAvailabilityApiArg = {
    chargePointId: string
    changeAvailabilityReq: ChangeAvailabilityReq
}
export type OcppCommandRemoteStartApiResponse = /** status 200  */ void
export type OcppCommandRemoteStartApiArg = {
    chargePointId: string
    remoteStartTransactionReq: RemoteStartTransactionReq
}
export type OcppCommandRemoteStopApiResponse = /** status 200  */ void
export type OcppCommandRemoteStopApiArg = {
    chargePointId: string
    remoteStopTransactionReq: RemoteStopTransactionReq
}
export type OcppCommandTriggerMessageApiResponse = /** status 200  */ void
export type OcppCommandTriggerMessageApiArg = {
    chargePointId: string
    triggerMessageReq: TriggerMessageReq
}
export type OcppCommandUpdateFirmwareApiResponse = /** status 200  */ void
export type OcppCommandUpdateFirmwareApiArg = {
    chargePointId: string
    updateFirmwareReq: UpdateFirmwareReq
}
export type OcppCommandTestApiResponse = /** status 200  */ void
export type OcppCommandTestApiArg = {
    chargePointId: string
    testOcppCommand: TestOcppCommand
}
export type OcppCommandUpdateConfigKeysApiResponse = /** status 200  */ void
export type OcppCommandUpdateConfigKeysApiArg = {
    chargePointId: string
    body: KeyValue[]
}
export type OcppCommandUpdateConfigKeyApiResponse = /** status 200  */ void
export type OcppCommandUpdateConfigKeyApiArg = {
    chargePointId: string
    keyValue: KeyValue
}
export type OcppCommandInstallCertificateApiResponse = /** status 200  */ void
export type OcppCommandInstallCertificateApiArg = {
    chargePointId: string
    installCertificateReq: InstallCertificateReq
}
export type OcppCommandDeleteCertificateApiResponse = /** status 200  */ void
export type OcppCommandDeleteCertificateApiArg = {
    chargePointId: string
    deleteCertificateReq: DeleteCertificateReq
}
export type OcppCommandGetInstalledCertsApiResponse = /** status 200  */ void
export type OcppCommandGetInstalledCertsApiArg = {
    chargePointId: string
    getInstalledCertificateIdsReq: GetInstalledCertificateIdsReq
}
export type PartnerGetAllAllApiResponse = /** status 200  */ Partner[]
export type PartnerGetAllAllApiArg = {
    excludeOrphans?: boolean
}
export type PartnerCreatePartnerApiResponse = /** status 200  */ Partner | /** status 201  */ Partner
export type PartnerCreatePartnerApiArg = {
    partner: Partner
}
export type PartnerUpdatePartnerApiResponse = /** status 200  */ Partner
export type PartnerUpdatePartnerApiArg = {
    partner: Partner
}
export type PartnerGetAllApiResponse = /** status 200  */ PagedResultOfPartner
export type PartnerGetAllApiArg = {
    id?: number
    name?: string | null
    email?: string | null
    excludeOrphans?: boolean
    page?: number | null
    noOfItems?: number | null
}
export type PartnerGetByIdApiResponse = /** status 200  */ Partner
export type PartnerGetByIdApiArg = {
    partnerId: number
}
export type PartnerDeleteApiResponse = unknown
export type PartnerDeleteApiArg = {
    partnerId: number
}
export type PartnerGetCompaniesByPartnerIdApiResponse = /** status 200  */ Company[]
export type PartnerGetCompaniesByPartnerIdApiArg = {
    partnerId: number
    expand?: string | null
}
export type PartnerGetChargePointsPagedApiResponse = /** status 200  */ PagedResultOfChargePoint
export type PartnerGetChargePointsPagedApiArg = {
    partnerId: number
    id?: string | null
    name?: string | null
    ocppOnly?: boolean | null
    productId?: number | null
    PartnerId?: number | null
    companyId?: string | null
    companyName?: string | null
    installedFirmwareVersion?: string | null
    firmwareType?: FirmwareType | null
    imsiNumber?: string | null
    ownerName?: string | null
    ownerEmail?: string | null
    ownerMobile?: string | null
    status?: ChargePointStatus | null
    errorCode?: string | null
    page?: number | null
    noOfItems?: number | null
    column?: string | null
    sortOrder?: SortOrder
    expand?: string | null
}
export type PartnerGetAlarmsByPartnerIdApiResponse = /** status 200  */ Alarm[]
export type PartnerGetAlarmsByPartnerIdApiArg = {
    partnerId: number
    showConfirmed?: boolean
    showHidden?: boolean
    offset?: number
    noOfItems?: number
    expand?: string | null
}
export type PartnerSetAcknowledgedApiResponse = unknown
export type PartnerSetAcknowledgedApiArg = {
    partnerId: number
    alarmId: number
}
export type PartnerGetUsersByPartnerIdApiResponse = /** status 200  */ ChargePointUser[]
export type PartnerGetUsersByPartnerIdApiArg = {
    partnerId: number
}
export type PartnerCreatePartnerUserApiResponse = /** status 200  */ ChargePointUser
export type PartnerCreatePartnerUserApiArg = {
    partnerId: number
    chargePointUser: ChargePointUser
}
export type PartnerUpdatePartnerUserApiResponse = unknown
export type PartnerUpdatePartnerUserApiArg = {
    partnerId: number
    chargePointUser: ChargePointUser
}
export type PartnerAddUserAsPartnerApiResponse = unknown
export type PartnerAddUserAsPartnerApiArg = {
    partnerId: number
    userId: string
}
export type PartnerRemoveUserAsPartnerApiResponse = unknown
export type PartnerRemoveUserAsPartnerApiArg = {
    partnerId: number
    userId: string
}
export type PartnerGetLoadbalancerGroupsPagedApiResponse = /** status 200  */ PagedResultOfLoadBalancerGroup
export type PartnerGetLoadbalancerGroupsPagedApiArg = {
    partnerId: number
    id?: string | null
    name?: string | null
    companyName?: string | null
    page?: number | null
    noOfItems?: number | null
}
export type PartnerGetNfcTagsApiResponse = /** status 200  */ NfcTag[]
export type PartnerGetNfcTagsApiArg = {
    partnerId: number
}
export type PartnerGetExternalServersApiResponse = /** status 200  */ ExternalServer[]
export type PartnerGetExternalServersApiArg = {
    partnerId: number
}
export type PartnerGetPartnerCandidatesApiResponse = /** status 200  */ Partner[]
export type PartnerGetPartnerCandidatesApiArg = void
export type PartnerApprovePartnerCandidateApiResponse = /** status 200  */ Partner | /** status 201  */ Partner
export type PartnerApprovePartnerCandidateApiArg = {
    partnerCandidateId: number
}
export type PartnerDeletePartnerCandidateApiResponse = unknown
export type PartnerDeletePartnerCandidateApiArg = {
    partnerCandidateId: number
}
export type PoolCreateApiResponse = /** status 200  */ void | /** status 201  */ void
export type PoolCreateApiArg = {
    pool: Pool
}
export type PoolUpdateApiResponse = /** status 200  */ void
export type PoolUpdateApiArg = {
    pool: Pool
}
export type PoolGetByIdApiResponse = /** status 200  */ Pool
export type PoolGetByIdApiArg = {
    poolId: string
}
export type PoolConnectToUserApiResponse = /** status 200  */ void
export type PoolConnectToUserApiArg = {
    poolId: string
    userId: string
}
export type PoolDisconnectFromUserApiResponse = /** status 200  */ void
export type PoolDisconnectFromUserApiArg = {
    poolId: string
    userId: string
}
export type PoolDeletePoolApiResponse = /** status 200  */ void | /** status 204  */ void
export type PoolDeletePoolApiArg = {
    poolId: string
    companyId: string
}
export type PoolGetChargePointsApiResponse = /** status 200  */ ChargePoint[] | /** status 204  */ void
export type PoolGetChargePointsApiArg = {
    poolId: string
    expand?: string | null
}
export type ProductGetAllApiResponse = /** status 200  */ Product[]
export type ProductGetAllApiArg = {
    productVariant?: ProductVariantType
}
export type ProductGetOptionsApiResponse = /** status 200  */ ProductOption[]
export type ProductGetOptionsApiArg = {
    productVariant?: ProductVariantType
}
export type ProductGetHardwareversionsApiResponse = /** status 200  */ HardwareVersion[]
export type ProductGetHardwareversionsApiArg = {
    productId: number
}
export type ProductGetActiveHardwareVersionApiResponse = /** status 200  */ HardwareVersion[]
export type ProductGetActiveHardwareVersionApiArg = {
    productId: number
}
export type ProductSetActiveHardwareVersionApiResponse = /** status 200  */ void
export type ProductSetActiveHardwareVersionApiArg = {
    productId: number
    hardwareVersionId: number
}
export type ReportGetReportsApiResponse = /** status 200  */ Report[]
export type ReportGetReportsApiArg = {
    accessLevel?: AccessLevel
}
export type ReportGetReportsForAdminApiResponse = /** status 200  */ Report[]
export type ReportGetReportsForAdminApiArg = {
    selectedCompanyId?: string
}
export type ReportGetReportsForPartnerApiResponse = /** status 200  */ Report[]
export type ReportGetReportsForPartnerApiArg = {
    partnerId?: number | null
}
export type ReportGetReportDataApiResponse = /** status 200  */ void
export type ReportGetReportDataApiArg = {
    reportId: number
    partnerId?: number | null
    companyId?: string | null
    chargePointUserId?: string | null
    timeZone?: string | null
    timeZoneWindows?: string | null
    interval?: string | null
    to?: string | null
    from?: string | null
    fromInLocalTime?: string | null
    toInLocalTime?: string | null
}
export type ScheduleGetByIdApiResponse = /** status 200  */ Schedule
export type ScheduleGetByIdApiArg = {
    scheduleId: number
}
export type ScheduleDeleteScheduleApiResponse = /** status 200  */ void | /** status 204  */ void
export type ScheduleDeleteScheduleApiArg = {
    scheduleId: number
}
export type ScheduleGetByChargePointApiResponse = /** status 200  */ Schedule[]
export type ScheduleGetByChargePointApiArg = {
    chargePointId: string
}
export type ScheduleCreateScheduleApiResponse = /** status 200  */ void | /** status 201  */ Schedule
export type ScheduleCreateScheduleApiArg = {
    schedule: Schedule
}
export type ScheduleUpdateScheduleApiResponse = /** status 200  */ Schedule
export type ScheduleUpdateScheduleApiArg = {
    schedule: Schedule
}
export type ScheduleCopyScheduleFromChargePointApiResponse = /** status 200  */ void
export type ScheduleCopyScheduleFromChargePointApiArg = {
    chargePointId: string
}
export type SimulatedChargePointCreateSimulatedApiResponse = /** status 200  */
    | ChargePoint
    | /** status 201  */ ChargePoint
export type SimulatedChargePointCreateSimulatedApiArg = {
    productId: number
}
export type SmartChargingScheduleGetApiResponse = /** status 200  */ SmartChargingSchedule
export type SmartChargingScheduleGetApiArg = {
    chargePointId: string
    connectorId: number
}
export type SmartChargingScheduleClearApiResponse = /** status 200  */ void
export type SmartChargingScheduleClearApiArg = {
    chargePointId: string
    connectorId: number
}
export type SmartChargingScheduleGetByChargePointApiResponse = /** status 200  */ SmartChargingSchedule[]
export type SmartChargingScheduleGetByChargePointApiArg = {
    chargePointId: string
}
export type SmartChargingScheduleGetChargePointScheduleStatusApiResponse = /** status 200  */ SyncStatuses
export type SmartChargingScheduleGetChargePointScheduleStatusApiArg = {
    chargePointId: string
}
export type SmartChargingScheduleUpdateApiResponse = /** status 200  */ SmartChargingSchedule
export type SmartChargingScheduleUpdateApiArg = {
    smartChargingSchedule: SmartChargingSchedule
}
export type StatisticsGetChargePointStatusStatsForAllApiResponse = /** status 200  */ ChargePointStatusStatistics
export type StatisticsGetChargePointStatusStatsForAllApiArg = void
export type StatisticsGetChargePointTotalConsumptionLastSevenDaysApiResponse =
    /** status 200  */ TotalConsumptionDataPoint[]
export type StatisticsGetChargePointTotalConsumptionLastSevenDaysApiArg = {
    chargePointId: string
}
export type StatisticsGetChargePointStatusStatsForPartnerApiResponse = /** status 200  */ ChargePointStatusStatistics
export type StatisticsGetChargePointStatusStatsForPartnerApiArg = {
    partnerId: number
}
export type StatisticsGetUserTotalConsumptionApiResponse = /** status 200  */ TotalConsumptionDataPoint[]
export type StatisticsGetUserTotalConsumptionApiArg = {
    userId: string
    interval?: string | null
}
export type StatisticsGetChargePointStatusStatsForCompanyApiResponse = /** status 200  */ ChargePointStatusStatistics
export type StatisticsGetChargePointStatusStatsForCompanyApiArg = {
    companyId: string
}
export type StatisticsGetCompanyTotalConsumptionApiResponse = /** status 200  */ CompanyStatistics
export type StatisticsGetCompanyTotalConsumptionApiArg = {
    companyId: string
}
export type StatisticsGetUserUsageStatisticsApiResponse = /** status 200  */ UsageStatistics
export type StatisticsGetUserUsageStatisticsApiArg = {
    userId: string
}
export type StatisticsGetUserChargerUsageStatisticsApiResponse = /** status 200  */ ChargePointUsageDataPoint[]
export type StatisticsGetUserChargerUsageStatisticsApiArg = {
    userId: string
}
export type StatisticsGetCompanyUsageStatisticsApiResponse = /** status 200  */ UsageStatistics
export type StatisticsGetCompanyUsageStatisticsApiArg = {
    companyId: string
}
export type TroubleshootingGetErrorCodeTroubleshootingInfosApiResponse = /** status 200  */ ErrorCodeTroubleshootingInfo
export type TroubleshootingGetErrorCodeTroubleshootingInfosApiArg = {
    productId?: number | null
}
export type TroubleshootingGetErrorCodeTroubleshootingInfoApiResponse = /** status 200  */ ErrorCodeTroubleshootingInfo
export type TroubleshootingGetErrorCodeTroubleshootingInfoApiArg = {
    errorCode: string
    productId?: number | null
}
export type UserGetFilteredApiResponse = /** status 200  */ ChargePointUser[]
export type UserGetFilteredApiArg = {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    isSuperAdmin?: boolean | null
    noOfItems?: number | null
}
export type UserUpdateLoggedInUserApiResponse = /** status 200  */ void
export type UserUpdateLoggedInUserApiArg = {
    chargePointUser: ChargePointUser
}
export type UserGetUserByIdApiResponse = /** status 200  */ ChargePointUser
export type UserGetUserByIdApiArg = {
    userId: string
    companyId?: string | null
}
export type UserGetUserSettingsApiResponse = /** status 200  */ void
export type UserGetUserSettingsApiArg = {
    userId: string
    companyId?: string | null
}
export type UserUpdateUserSettingsApiResponse = /** status 200  */ void
export type UserUpdateUserSettingsApiArg = {
    userId: string
    companyId?: string | null
    body: UserSettingModel[]
}
export type UserLoggedInUserIsCompanyMemberApiResponse = /** status 200  */ Pool[]
export type UserLoggedInUserIsCompanyMemberApiArg = void
export type UserGetPoolsForSelectedCompanyApiResponse = /** status 200  */ Pool[]
export type UserGetPoolsForSelectedCompanyApiArg = {
    userId: string
    companyId?: string
}
export type UserGetForUserByNfcAccessWithNfcAccessInfoApiResponse = /** status 200  */ ChargePoint[]
export type UserGetForUserByNfcAccessWithNfcAccessInfoApiArg = {
    userId: string
    companyId: string
}
export type UserGetOwnedChargePointsApiResponse = /** status 200  */ ChargePoint[]
export type UserGetOwnedChargePointsApiArg = {
    expand?: string | null
}
export type UserGetOwnedChargePointsWithIdsApiResponse = /** status 200  */ ChargePoint[]
export type UserGetOwnedChargePointsWithIdsApiArg = {
    expand?: string | null
    body: string[]
}
export type UserIsUserHasChargePointsApiResponse = /** status 200  */ void
export type UserIsUserHasChargePointsApiArg = void
export type UserGetUserNotificationsApiResponse = /** status 200  */ UserNotification[]
export type UserGetUserNotificationsApiArg = {
    filter?: UserNotificationFilters | null
}
export type UserGetOwnNfcTagsApiResponse = /** status 200  */ NfcTag[]
export type UserGetOwnNfcTagsApiArg = void
export type UserUpdateOwnNfcTagsApiResponse = /** status 200  */ void
export type UserUpdateOwnNfcTagsApiArg = {
    body: NfcTag[]
}
export type UserUpdateUserGreenChargingSettingApiResponse = /** status 200  */ void
export type UserUpdateUserGreenChargingSettingApiArg = {
    userId: string
    companyId?: string | null
}
export type UserRemoveChargePointAccessApiResponse = /** status 200  */ void
export type UserRemoveChargePointAccessApiArg = {
    chargePointId: string
}
export type UserGetLatestRejectedTagApiResponse = /** status 200  */ void
export type UserGetLatestRejectedTagApiArg = void
export type UserSetAllNotificationsToReadForUserApiResponse = /** status 200  */ void
export type UserSetAllNotificationsToReadForUserApiArg = void
export type UserSetNotificationToReadForUserApiResponse = /** status 200  */ void
export type UserSetNotificationToReadForUserApiArg = {
    notificationId: number
}
export type UserAcceptTermsApiResponse = /** status 200  */ void
export type UserAcceptTermsApiArg = void
export type UserIsFavouriteApiResponse = /** status 200  */ void
export type UserIsFavouriteApiArg = {
    chargePointId: string
}
export type UserAddFavouriteApiResponse = /** status 200  */ void
export type UserAddFavouriteApiArg = {
    chargePointId: string
}
export type UserRemoveFavouriteApiResponse = /** status 200  */ void | /** status 204  */ void
export type UserRemoveFavouriteApiArg = {
    chargePointId: string
}
export type UserGetFavouritesApiResponse = /** status 200  */ ChargePoint[]
export type UserGetFavouritesApiArg = void
export type UserActivateUserApiResponse = /** status 200  */ void
export type UserActivateUserApiArg = {
    userId: string
}
export type UserGetPartnerCandidateApiResponse = /** status 200  */ Partner
export type UserGetPartnerCandidateApiArg = void
export type SeverityType = 'Error' | 'Warning' | 'Information'
export type AlarmDefinition = {
    id?: number
    name?: string | null
    description?: string | null
    code?: number
    messageMatchingPattern?: string | null
    severityType?: SeverityType
}
export type Company = {
    id?: string
    name?: string | null
    description?: string | null
    email?: string | null
    phone?: string | null
    partnerId?: number
    partnerName?: string | null
    kWhPrice?: number | null
    ownerReadOnly?: boolean
    contactPerson?: string | null
    address1?: string | null
    address2?: string | null
    comment?: string | null
    onlineCount?: number
    offlineCount?: number
    chargingCount?: number
}
export type Alarm = {
    id?: number
    chargePointId?: string | null
    acknowledged?: string | null
    acknowledgedBy?: string | null
    alarmDefinition?: AlarmDefinition | null
    noOfOccurences?: number
    firstOccurence?: string
    lastOccurence?: string
    company?: Company | null
}
export type ProblemDetails = {
    type?: string | null
    title?: string | null
    status?: number | null
    detail?: string | null
    instance?: string | null
    extensions?: {
        [key: string]: any
    }
    [key: string]: any | null
}
export type ApiCustomer = {
    id?: number
    apiKey?: string | null
    hashedApiKey?: string | null
    enabled?: boolean
    validFrom?: string
    validTo?: string
    name?: string | null
    description?: string | null
    email?: string | null
    callbackEnabled?: boolean
    callbackBaseUrl?: string | null
    callbackAuthHeaderKey?: string | null
    callbackAuthHeaderValue?: string | null
}
export type PhaseMode = '1P' | '3P' | '2P'
export type OcppServerMode = 'None' | 'External' | 'Internal'
export type ProductConfigurationTemplate = {
    id?: number
    productId?: number
    monitorArticleId?: string | null
    name?: string | null
    description?: string | null
    phaseMode?: PhaseMode
    maxCurrent?: number
    active?: boolean
    schukoEnabled?: boolean
    wifiEnabled?: boolean
    nfcEnabled?: boolean
    bluetoothEnabled?: boolean
    ocppServerMode?: OcppServerMode
}
export type UserCredentials = {
    email?: string | null
    password?: string | null
    hostName?: string | null
}
export type PasswordChange = {
    currentPassword?: string | null
    newPassword?: string | null
}
export type ChargePointUserStatus = 'New' | 'Valid' | 'Locked'
export type Role = {
    claim?: string | null
    companyId?: string
    chargePointUserId?: string
}
export type ChargePointUser = {
    id?: string
    firstName?: string | null
    lastName?: string | null
    password?: string | null
    passwordChange?: PasswordChange | null
    mobile?: string | null
    email?: string | null
    userType?: string | null
    partnerId?: number | null
    personalAccept?: string | null
    companyAccept?: string | null
    lastLogin?: string | null
    userName?: string | null
    fullName?: string | null
    hostName?: string | null
    claims?: string[] | null
    features?: string[] | null
    isPartner?: boolean
    userStatus?: ChargePointUserStatus
    roles?: Role[] | null
    isSuperAdmin?: boolean
    roleInCompany?: string | null
    passwordChangeableForAdmin?: boolean | null
}
export type Partner = {
    id?: number
    name?: string | null
    description?: string | null
    email?: string | null
    phone?: string | null
    hostName?: string | null
}
export type ChargePointStatus =
    | 'Online'
    | 'Offline'
    | 'Charging'
    | 'Available'
    | 'Faulted'
    | 'Unavailable'
    | 'Connected'
    | 'Preparing'
    | 'SuspendedEV'
    | 'SuspendedEVSE'
    | 'Finishing'
    | 'Reserved'
export type ChargePointGeneral = {
    id: string
    name?: string | null
    productId?: number
    ownerId?: string | null
    companyId?: string | null
    installedFirmwareVersion?: string | null
    availableFirmwareVersion?: string | null
    lastHeartbeat?: string | null
    offlineSince?: string | null
    fwAvailableTimestamp?: string | null
    firmwareLastUpdated?: string | null
    ocppServerMode?: OcppServerMode
    isOcpp?: boolean
    companyName?: string | null
    chargePointStatus?: ChargePointStatus
}
export type ConnectorPhaseRotations = 'RST' | 'RTS' | 'SRT' | 'STR' | 'TRS' | 'TSR'
export type EarthingSystem = 'TN' | 'IT'
export type ChargePointMeterType = 'UNKNOWN' | 'ATM90E32AS' | 'CA_MID_01' | 'CA_MID_01_DE'
export type ProductType = 'Halo1' | 'Ray' | 'Halo2' | 'Aura' | 'AmpGuard' | 'Enegic' | 'Dawn' | 'AmpGuard2' | 'Luna'
export type ConnectorFunctionType = 'Charger' | 'Schuko'
export type Phase = 'L1' | 'L2' | 'L3' | 'L123' | 'L1L2' | 'L2L3' | 'L3L1'
export type ConnectorType = 'Type1' | 'Type2' | 'ThirdPartyType1' | 'ThirdPartyType2' | 'Type2Outlet'
export type OnOffMode = 'Off' | 'On' | 'Schedule'
export type EvConnectorState =
    | 'A1NotConnected'
    | 'A2NotConnected'
    | 'B1Connected'
    | 'B2Connected'
    | 'C1ConnectedRequestingCharge'
    | 'C2ConnectedCharging'
    | 'DRequestingVentilation'
    | 'EPilotShortcircuit'
    | 'FMinus12Volt'
    | 'B1NoDiode'
    | 'C1WaitStop'
    | 'C2WaitLock'
export type SessionType = 'OnePhase' | 'ThreePhase' | 'Schuko'
export type ThreePhaseCurrent = {
    current1?: number
    current2?: number
    current3?: number
    cosPhi1?: number
    cosPhi2?: number
    cosPhi3?: number
}
export type ChargePointOcppStatus =
    | 'Available'
    | 'Preparing'
    | 'Charging'
    | 'SuspendedEVSE'
    | 'SuspendedEV'
    | 'Finishing'
    | 'Reserved'
    | 'Unavailable'
    | 'Faulted'
export type Connector = {
    connectorId?: number
    chargePointId?: string | null
    name?: string | null
    connectorFunctionType?: ConnectorFunctionType
    minCurrent?: number
    internalLoadbalancerMinCurrent?: number
    factoryCurrent?: number
    factoryPhaseMode?: PhaseMode
    installationCurrent?: number
    installationPhase?: Phase
    installationPhaseMode?: PhaseMode
    userCurrent?: number | null
    loadBalancerPhase?: Phase | null
    loadBalancerCurrent?: number | null
    loadBalancerConnectorOn?: boolean | null
    loadBalancerOfflineCurrent?: number | null
    loadBalancerOfflinePhase?: Phase | null
    connectorType?: ConnectorType | null
    mode?: OnOffMode
    activatedByScheduleId?: number | null
    nfcLocked?: boolean
    defaultNfcTagId?: string | null
    remoteStartRequested?: boolean
    remoteStopRequested?: boolean
    connectorUserId?: string | null
    externalId?: string | null
    cableLocked?: boolean
    hasSchedule?: boolean | null
    currentCurrent?: number
    factoryMaxPower?: number
    installationMaxPower?: number
    onByUser?: boolean
    offByUser?: boolean
    onBySchedule?: boolean
    offBySchedule?: boolean
    enabled?: boolean
    onByUserOrSchedule?: boolean
    onInStatusResponse?: boolean
    evConnectorState?: EvConnectorState | null
    voltageRaw1?: number
    voltageRaw2?: number
    voltageRaw3?: number
    currentRaw1?: number
    currentRaw2?: number
    currentRaw3?: number
    errorCode?: string | null
    current1?: number
    voltage1?: number
    current2?: number
    voltage2?: number
    current3?: number
    voltage3?: number
    sessionType?: SessionType | null
    currentAdjust?: number | null
    voltageAdjust?: number | null
    totalConsumptionKwh?: number
    isCharging?: boolean
    chargingCurrent?: ThreePhaseCurrent | null
    totalConsumptionRaw?: number
    ocppStatus?: ChargePointOcppStatus | null
    connectorUserName?: string | null
    connectorUserEmail?: string | null
    connectorUserMobile?: string | null
    connectorUserInfo?: string | null
    hasNfcAccess?: boolean
}
export type StatusInterval = {
    description?: string | null
    id?: number
    intervalSeconds?: number
}
export type ChargePointAdditionalInfo = {
    chargePointId?: string | null
    description?: string | null
    installationDate?: string | null
    hasServiceAgreement?: boolean
    externalServerId?: number | null
    partnerDescription?: string | null
    wifiSsid?: string | null
    wifiPassword?: string | null
    ocppStopTransactionOnInvalidId?: boolean
    ocppClockAlignedDataInterval?: number
    ocppConnectionTimeOut?: number
    ocppLocalAuthorizeOffline?: boolean
    ocppLocalPreAuthorize?: boolean
    ocppMeterValuesAlignedData?: boolean
    ocppMeterValuesSampledData?: boolean
    ocppResetRetries?: number
    ocppStopTxnAlignedData?: boolean
    ocppStopTxnSampledData?: boolean
    ocppTransactionMessageAttempts?: number
    ocppTransactionMessageRetryInterval?: number
    ocppUnlockConnectorOnEvSideDisconnect?: boolean
    ocppStopTransactionOnEvSideDisconnect?: boolean
    ocppAllowOfflineTxForUnknownId?: boolean
    ocppAuthorizationCacheEnabled?: boolean
    callbackEnabledApiCustomerId?: number | null
    partnerAccessible?: boolean
    statusIntervalId?: number
    statusInterval?: StatusInterval | null
    wakeUpEnabled?: boolean
    allowConnectCurrent?: boolean
    ocppHeartbeatInterval?: number | null
}
export type KeyValue = {
    key?: string | null
    readonly?: boolean
    value?: string | null
}
export type ChargePoint = ChargePointGeneral & {
    timeZone?: number
    poolId?: string | null
    ip?: string | null
    downLight?: boolean
    imsiNumber?: string | null
    chargingUnitSerialNumber?: string | null
    supplyCurrent?: number
    hardwareVersionId?: number
    hardwareVersion?: string | null
    installedFirmwareId?: number
    availableFirmwareId?: number
    wifiPassword?: string | null
    dimmer?: number
    ocppEnabled?: boolean
    debugEnabled?: boolean
    nfcEnabled?: boolean
    reboot?: boolean
    countrySettingsId?: number | null
    loadBalancerGroupId?: number | null
    startTest?: boolean
    processorId?: string | null
    ocppProxyServiceId?: number | null
    ignoreRelayWeld?: boolean
    wifiSignalStrength?: string | null
    serialNumber?: string | null
    templateId?: number
    hardwareCompatibilityVersion?: number
    inActiveLoadBalancerGroup?: boolean
    loadBalancerGroupName?: string | null
    productName?: string | null
    poolName?: string | null
    ownerReadOnly?: boolean
    ownerName?: string | null
    ownerEmail?: string | null
    ownerMobile?: string | null
    connectorPhaseRotation?: ConnectorPhaseRotations | null
    earthingSystem?: EarthingSystem
    meterType?: ChargePointMeterType
    productType?: ProductType
    isCharging?: boolean
    connectors?: Connector[] | null
    additionalInfo?: ChargePointAdditionalInfo | null
    partnerInfo?: Partner | null
    ocppConfigKeys?: KeyValue[] | null
    acPhaseSwitchingSupported?: boolean
    numberOfConnectors?: number | null
    maxPhases?: number | null
}
export type ChargerInformation = {
    description?: string | null
    partnerDescription?: string | null
    externalServerId?: number | null
    allowConnectCurrent?: boolean
    callbackEnabledApiCustomerId?: number | null
    hasServiceAgreement?: boolean
    partnerAccessible?: boolean
    installationDate?: string | null
    statusIntervalId?: number
    wifiSsid?: string | null
    wifiPassword?: string | null
    countrySettingsId?: number | null
    poolId?: string | null
    ownerId?: string | null
    debugEnabled?: boolean
    hardwareVersionId?: number
    connector1OwnerId?: string | null
    connector2OwnerId?: string | null
    connector1ExternalId?: string | null
    connector2ExternalId?: string | null
}
export type PagedResultOfChargePoint = {
    items?: ChargePoint[] | null
    totalCount?: number
    pageSize?: number | null
    pageIndex?: number | null
}
export type FirmwareType = 'None' | 'Master' | 'Slave' | 'Wifi'
export type SortOrder = 'Ascending' | 'Descending'
export type ChargePointCalibration = {
    chargePointId?: string | null
    timestamp?: string
    currentAdjust?: number
    voltageAdjust?: number
}
export type SetupCallback = {
    isEnabled?: boolean
    apiCustomerId?: number | null
}
export type ChargingSession = {
    id?: number
    chargePointId?: string | null
    connectorId?: number
    startTime?: string
    endTime?: string | null
    totalConsumption?: number | null
    userId?: string
    nfcUid?: string | null
    sessionType?: SessionType
    ocppTransactionId?: number | null
    chargePointName?: string | null
    productId?: number
    connectorName?: string | null
    connectorFunctionType?: ConnectorFunctionType
    currentAdjust?: number | null
    voltageAdjust?: number | null
    totalConsumptionKwh?: number | null
    chargePointUserName?: string | null
    companyId?: string | null
}
export type ChargingSessionsWithCount = {
    sessions?: ChargingSession[] | null
    totalCount?: number
}
export type NfcTag = {
    id?: string | null
    chargePointUserId?: string | null
    active?: boolean
    timestamp?: string
    companyId?: string | null
    comment?: string | null
    partnerId?: number | null
    externalId?: string | null
    firstName?: string | null
    lastName?: string | null
    fullName?: string | null
    companyName?: string | null
}
export type ConnectorScheduleOverrideStatus = {
    connectorId?: number
    isOverridden?: boolean
}
export type ChangeProcessorId = {
    oldProcessorId?: string | null
    newProcessorId?: string | null
}
export type MaintenanceLogAccessLevel = 'Support' | 'Partner' | 'Admin'
export type MaintenanceLog = {
    id?: number
    chargePointId?: string | null
    message?: string | null
    sign?: string | null
    timestamp?: string
    userId?: string
    accessLevel?: MaintenanceLogAccessLevel
}
export type ApprovementType = 'Programmed' | 'Calibrated' | 'ProductionTested'
export type ChargePointProductionInfo = {
    id?: number
    chargePointId?: string | null
    siteName?: string | null
    approved?: string
    approvedBy?: string | null
    approvementType?: ApprovementType
    setupSnapshot?: string | null
}
export type UpdateChargerFirmwareModel = {
    retries?: number | null
    retrieveDate?: string | null
    retryInterval?: number | null
    firmwareId?: number
}
export type UpdateChargerFirmwareOnSeveralModel = UpdateChargerFirmwareModel & {
    chargePointIds?: string[] | null
}
export type MessageDirection = 'Incoming' | 'Outgoing'
export type OcppCommunicationLog = {
    id?: number
    chargePointId?: string | null
    ocppUniqueId?: string | null
    messageDirection?: MessageDirection
    messageType?: number
    message?: string | null
    action?: string | null
    timestamp?: string
}
export type ChargePointData = {
    chargePointId?: string | null
    connectorId?: number | null
    dataTypeId?: number
    timestamp?: string
    rawData?: string | null
    rawAsString?: string | null
    dataTypeDescription?: string | null
    formattedData?: string | null
    showData?: boolean
}
export type DataTypeFilter = {
    wifiInfo?: boolean
    temperature?: boolean
    processorVoltage?: boolean
    relayMeasures?: boolean
    pwmStateShifts?: boolean
    pwm?: boolean
    gfcidcWaveform?: boolean
    clientInfo?: boolean
}
export type DataLogFilter = {
    dataType?: DataTypeFilter | null
}
export type Consumption = {
    id?: number
    chargingCurrent?: number
    chargingCurrent2?: number
    chargingCurrent3?: number
    chargingVoltage?: number
    chargingVoltage2?: number
    chargingVoltage3?: number
    chargingSessionId?: number | null
    timestamp?: string
    totalConsumption?: number
    evConnectorState?: EvConnectorState | null
    sessionType?: SessionType | null
    phase?: Phase | null
    rawData?: string | null
    connectorId?: number
}
export type CircuitBoard = {
    id?: number
    serialNumber?: number
    productId?: number
    createdDate?: string
    hardwareVersion?: string | null
    macAddress?: number | null
    details?: string | null
    signature?: string | null
    company?: string | null
    typeId?: number
    processorId?: string | null
    macAddressAsBytes?: string | null
}
export type CompanyPreview = {
    id?: string
    partnerId?: number | null
    name?: string | null
}
export type DeliveryInterval = 'Day' | 'Month' | 'Year' | 'Quarter'
export type ReportDeliveryType = 'Email' | 'Ftp'
export type ReportDeliverySchedule = {
    id?: number
    reportId?: number
    partnerId?: number | null
    companyId?: string | null
    deliveryInterval?: DeliveryInterval
    receiverEmail?: string | null
    receiverName?: string | null
    lastSent?: string
    active?: boolean
    deliveryType?: ReportDeliveryType
    ftpUserName?: string | null
    ftpPassword?: string | null
    ftpUrl?: string | null
    timeZone?: string | null
    mailSubject?: string | null
}
export type DimmerValue = 'Off' | 'Low' | 'Medium' | 'High'
export type ConnectorImport = {
    loadBalancerOfflineCurrent?: number | null
    loadBalancerOfflinePhase?: Phase | null
    connectorId?: number
    name?: string | null
    installationCurrent?: number | null
    installationPhase?: Phase | null
    installationPhaseMode?: PhaseMode | null
    mode?: OnOffMode | null
    nfcLocked?: boolean | null
    connectorType?: ConnectorType | null
}
export type ChargePointImport = {
    id?: string | null
    wifiPassword?: string | null
    name?: string | null
    downLight?: boolean | null
    dimmer?: DimmerValue | null
    supplyCurrent?: number | null
    description?: string | null
    installationDate?: string | null
    hasServiceAgreement?: boolean | null
    wifiSsid?: string | null
    wifiSsidPassword?: string | null
    partnerDescription?: string | null
    warningReason?: string[] | null
    errorReason?: string[] | null
    hasError?: boolean
    connectors?: ConnectorImport[] | null
    productType?: ProductType
    ocppServerMode?: OcppServerMode
}
export type Pool = {
    id?: string
    name?: string | null
    companyId?: string
    hasNfcAccess?: boolean
}
export type IDetailedChargingSession = {
    chargePointName?: string | null
    connectorName?: string | null
    connectorFunctionType?: ConnectorFunctionType
    currentAdjust?: number | null
    voltageAdjust?: number | null
    totalConsumptionKwh?: number | null
    chargePointUserName?: string | null
}
export type LoadBalancerGroup = {
    id?: number
    name?: string | null
    description?: string | null
    active?: boolean
    allowedPhases?: string | null
    allowedPhasesParsed?: Phase[] | null
    chargePointsIncludedInMeasurement?: boolean
    companyId?: string
    companyName?: string | null
    currentLimitSchemeId?: number | null
    deviceControlledMaxCurrentPerPhase?: number | null
    earthingSystem?: EarthingSystem | null
    isAliveTimestamp?: string
    isOcppLoadBalancer?: boolean
    maxCurrentPerPhase?: number
    minCurrentPerChargePoint?: number
    measurementDeviceId?: number | null
    schukoEnabled?: boolean
    serviceId?: number | null
    snapshotIntervalSeconds?: number | null
    supplyMaxCurrent?: number | null
}
export type PagedResultOfLoadBalancerGroup = {
    items?: LoadBalancerGroup[] | null
    totalCount?: number
    pageSize?: number | null
    pageIndex?: number | null
}
export type ChargePointCredentials = {
    id?: string | null
    wifiPassword?: string | null
}
export type ChargePointNfcInfo = {
    chargePointId?: string | null
    connectorId?: number
    hasNfcAccess?: boolean
}
export type MeasurementDeviceCredentials = {
    serialNumber?: string | null
    password?: string | null
}
export type MeasurementDeviceStatus = 'Online' | 'Offline'
export type MeasurementDevicesGetModel = {
    id?: number
    serialNumber?: string
    lastSeen?: string | null
    name?: string | null
    description?: string | null
    companyId?: string | null
    companyName?: string | null
    partnerId?: number | null
    productId?: number
    active?: boolean
    loadBalancerGroupId?: number | null
    status?: MeasurementDeviceStatus
}
export type CompanyTransferRequest = {
    companyId?: string
    companyName?: string | null
    toPartnerId?: number
    toPartnerName?: string | null
    fromPartnerId?: number
    fromPartnerName?: string | null
    validTo?: string
    requestBy?: string
    requestByName?: string | null
    timestamp?: string
}
export type ComputedAccessType =
    | 'Undefined'
    | 'TagAccessToChargePoint'
    | 'TagAccessToPool'
    | 'UserAccessToChargePoint'
    | 'UserAccessToPool'
    | 'UserAccessToChargePointThroughPartner'
    | 'UserAccessToPoolThroughPartner'
    | 'UserOwnerOfChargePoint'
    | 'UserOwnerOfConnector'
    | 'PublicCharging'
    | 'RemoteStart'
export type ComputedNfcAccess = {
    chargePointId?: string | null
    connectorId?: number
    nfcTagId?: string | null
    accessType?: ComputedAccessType
}
export type CountrySettings = {
    id?: number
    countryCode?: string | null
    countryName?: string | null
    schuckoCurrentLimit?: number
    ignoreRelayWeld?: boolean
}
export type ExternalServer = {
    id?: number
    url?: string | null
    description?: string | null
    initialAuthorizationKey?: string | null
}
export type FirmwareStatus = 'New' | 'UpdateToAvailable' | 'UpdateToUnavailable' | 'UpdateFromUnavailable'
export type FirmwareGeneralModel = {
    id?: number
    firmwareVersion?: string | null
    firmwareType?: FirmwareType
    description?: string | null
    uploadDate?: string
    productId?: number
    ocppEnabledFirmware?: boolean
    firmwareServerLocationId?: number | null
    firmwareStatus?: FirmwareStatus
    chargePointsCount?: number
}
export type PagedResultOfFirmwareGeneralModel = {
    items?: FirmwareGeneralModel[] | null
    totalCount?: number
    pageSize?: number | null
    pageIndex?: number | null
}
export type GreenOnlyInterval = {
    startHour?: number
    startMinute?: number
    endHour?: number
    endMinute?: number
    timeZone?: string | null
}
export type GreenChargingMeasurementDevice = {
    serialNumber?: string | null
    name?: string | null
}
export type GreenChargingGroup = {
    id?: number
    organizationId?: string | null
    controlEnabled?: boolean
    greenChargingOn?: boolean
    alwaysGreenOnly?: boolean
    greenOnlyInterval?: GreenOnlyInterval | null
    measurementDevice?: GreenChargingMeasurementDevice | null
}
export type GreenChargingStatus = {
    greenChargingOn?: boolean
    isWithinGreenOnlyInterval?: boolean
}
export type GreenChargingChargePointStatus = {
    id?: string | null
    greenChargingStatus?: GreenChargingStatus | null
}
export type UpdateGreenOnlyIntervalModel = {
    startHour?: number
    startMinute?: number
    endHour?: number
    endMinute?: number
    timeZone?: string | null
}
export type UpdateGreenChargingGroup = {
    id?: number
    greenChargingOn?: boolean
    alwaysGreenOnly?: boolean
    greenOnlyInterval?: UpdateGreenOnlyIntervalModel | null
}
export type MeasurementDeviceSetupModel = {
    id?: number
    serialNumber?: string | null
    name?: string | null
    description?: string | null
    password?: string | null
    ignoreCurrentDirection?: boolean
}
export type SetupModel = {
    company?: Company | null
    loadBalancerGroup?: LoadBalancerGroup | null
    measurementDevice?: MeasurementDeviceSetupModel | null
    chargePoints?: ChargePoint[] | null
}
export type CurrentLimitSchemeItem = {
    id?: number
    currentLimitSchemeId?: number
    current?: number
    startHours?: number
    startMinutes?: number
}
export type CurrentLimitScheme = {
    currentLimitSchemeItems?: CurrentLimitSchemeItem[] | null
    id?: number
    active?: boolean
    timeZone?: string | null
}
export type LoadBalancerChargePointsImportModel = {
    isAutorotate?: boolean
    rotationPhase?: Phase
    chargePoints?: ChargePointImport[] | null
}
export type LoadBalancerSnapshot = {
    id?: number
    loadBalancerGroupId?: number
    chargingCurrent1?: number
    chargingCurrent2?: number
    chargingCurrent3?: number
    measurementCurrent1?: number
    measurementCurrent2?: number
    measurementCurrent3?: number
    currentLimit1?: number
    currentLimit2?: number
    currentLimit3?: number
    timestamp?: string
}
export type ApplicationLog = {
    id?: number
    chargePointUserId?: string
    timestamp?: string
    message?: string | null
    detailedMessage?: string | null
    stacktrace?: string | null
    logLevel?: string | null
    ip?: string | null
    origin?: string | null
    version?: string | null
    username?: string | null
    chargePointId?: string | null
    connectorId?: number | null
    loadbalancerGroupId?: number | null
    originShort?: string | null
}
export type LogLevelFilter = {
    debug?: boolean
    info?: boolean
    warn?: boolean
    error?: boolean
}
export type OriginFilter = {
    lb?: boolean
    capi?: boolean
    measure?: boolean
    device?: boolean
    web?: boolean
    ocpp?: boolean
    batch?: boolean
}
export type LogFilter = {
    logLevel?: LogLevelFilter | null
    origin?: OriginFilter | null
}
export type MeasurementDeviceExtendedGetModel = {
    id?: number
    serialNumber?: string
    active?: boolean
    companyId?: string | null
    chargePointUserId?: string | null
    remoteViewerId?: string | null
    name?: string | null
    description?: string | null
    password?: string
    productId?: number
    created?: string
    detailedDescription?: string | null
    pointOfInstallation?: string | null
    ignoreCurrentDirection?: boolean | null
    measurementDeviceBatchId?: number | null
    companyName?: string | null
    partnerId?: number | null
    partnerName?: string | null
    loadBalancerGroupId?: number | null
    loadBalancerGroupName?: string | null
    loadBalancerGroupSupplyMaxCurrent?: number | null
    manufacturer?: string
    lastSeen?: string | null
    status?: MeasurementDeviceStatus
}
export type CreateMeasurementDeviceApiModel = {
    name?: string | null
    description?: string | null
    detailedDescription?: string | null
    pointOfInstallation?: string | null
    ignoreCurrentDirection?: boolean | null
    companyId?: string | null
    type?: number
}
export type UpdateMeasurementDeviceModel = {
    active?: boolean
    name?: string | null
    description?: string | null
    detailedDescription?: string | null
    pointOfInstallation?: string | null
    ignoreCurrentDirection?: boolean | null
    companyId?: string | null
    chargePointUserId?: string | null
}
export type MeasurementDeviceInfoUserGetModel = {
    id?: number
    active?: boolean
    name?: string | null
    description?: string | null
    loadBalancerGroupSupplyMaxCurrent?: number | null
    lastSeen?: string | null
}
export type MeasurementLatestModel = {
    id?: string
    serialNumber?: string
    deviceId?: number
    timestamp?: string
    current1?: number
    current2?: number
    current3?: number
    voltage1?: number
    voltage2?: number
    voltage3?: number
    cosphi1?: number
    cosphi2?: number
    cosphi3?: number
    signalStrength?: number
    version?: number
    energyWh?: number | null
}
export type MeasurementLatestDayModel = {
    timestamp?: string
    current1?: number
    current2?: number
    current3?: number
}
export type MeasurementDeviceGetModel = {
    serialNumber?: string
    active?: boolean
    companyId?: string | null
    chargePointUserId?: string | null
    remoteViewerId?: string | null
    name?: string | null
    description?: string | null
    password?: string
    productId?: number
    created?: string
    detailedDescription?: string | null
    pointOfInstallation?: string | null
    ignoreCurrentDirection?: boolean | null
    measurementDeviceBatchId?: number | null
    id?: number
}
export type AuthorizeResult = 'Accept' | 'Reject' | 'Wait' | 'Error' | 'NotFound' | 'Timeout'
export type RejectedAccess = {
    nfcTagId?: string | null
    chargePointId?: string | null
    connectorId?: number
    timeStamp?: string
    authorizeResult?: AuthorizeResult
}
export type PagedResultOfRejectedAccess = {
    items?: RejectedAccess[] | null
    totalCount?: number
    pageSize?: number | null
    pageIndex?: number | null
}
export type Notification = {
    id?: number
    title?: string | null
    description?: string | null
    timestamp?: string
    publishedTimestamp?: string | null
    isPublished?: boolean
}
export type NotificationFilterType = 'all' | 'published' | 'unpublished'
export type ResetType = 'Hard' | 'Soft'
export type ResetReq = {
    type: ResetType
}
export type DataTransferReq = {
    vendorId: string
    messageId?: string | null
    data?: string | null
}
export type GetConfigurationReq = {
    key?: string[] | null
}
export type GetDiagnosticsReq = {
    location: string
    retries?: number | null
    retryInterval?: number | null
    startTime?: string | null
    stopTime?: string | null
}
export type UnlockConnectorReq = {
    connectorId: number
}
export type AvailabilityType = 'Inoperative' | 'Operative'
export type ChangeAvailabilityReq = {
    connectorId: number
    type: AvailabilityType
}
export type ChargingProfilePurposeType = 'ChargePointMaxProfile' | 'TxDefaultProfile' | 'TxProfile'
export type ChargingProfileKindType = 'Absolute' | 'Recurring' | 'Relative'
export type RecurrencyKindType = 'Daily' | 'Weekly'
export type ChargingRateUnitType = 'W' | 'A'
export type ChargingSchedulePeriod = {
    startPeriod?: number
    limit?: number
    numberPhases?: number | null
    phaseToUse?: number | null
}
export type ChargingSchedule = {
    duration?: number | null
    startSchedule?: string | null
    chargingRateUnit?: ChargingRateUnitType
    chargingSchedulePeriod?: ChargingSchedulePeriod[] | null
    minChargingRate?: number | null
}
export type ChargingProfile = {
    chargingProfileId?: number
    transactionId?: number | null
    stackLevel?: number
    chargingProfilePurpose?: ChargingProfilePurposeType
    chargingProfileKind?: ChargingProfileKindType
    recurrencyKind?: RecurrencyKindType | null
    validFrom?: string | null
    validTo?: string | null
    chargingSchedule?: ChargingSchedule | null
}
export type RemoteStartTransactionReq = {
    connectorId?: number
    idTag: string
    chargingProfile?: ChargingProfile | null
}
export type RemoteStopTransactionReq = {
    transactionId: number
}
export type MessageTrigger =
    | 'BootNotification'
    | 'DiagnosticsStatusNotification'
    | 'FirmwareStatusNotification'
    | 'Heartbeat'
    | 'MeterValues'
    | 'StatusNotification'
export type TriggerMessageReq = {
    requestedMessage: MessageTrigger
    connectorId?: number | null
}
export type UpdateFirmwareReq = {
    location: string
    retries?: number | null
    retrieveDate: string
    retryInterval?: number | null
}
export type CallAction =
    | 'ChangeAvailability'
    | 'ChangeConfiguration'
    | 'ClearCache'
    | 'DataTransfer'
    | 'GetConfiguration'
    | 'RemoteStartTransaction'
    | 'RemoteStopTransaction'
    | 'Reset'
    | 'UnlockConnector'
    | 'GetDiagnostics'
    | 'UpdateFirmware'
    | 'SignedUpdateFirmware'
    | 'GetLog'
    | 'TriggerMessage'
    | 'SetChargingProfile'
    | 'ClearChargingProfile'
    | 'InstallCertificate'
    | 'DeleteCertificate'
    | 'GetInstalledCertificateIds'
export type TestOcppCommand = {
    action?: CallAction
    payload?: string | null
}
export type CertificateUseEnumType = 'CentralSystemRootCertificate' | 'ManufacturerRootCertificate'
export type InstallCertificateReq = {
    certificateType: CertificateUseEnumType
    certificate: string
}
export type HashAlgorithmEnumType = 'SHA256' | 'SHA384' | 'SHA512'
export type CertificateHashDataType = {
    hashAlgorithm: HashAlgorithmEnumType
    issuerNameHash: string
    issuerKeyHash: string
    serialNumber: string
}
export type DeleteCertificateReq = {
    certificateHashData: CertificateHashDataType
}
export type GetInstalledCertificateIdsReq = {
    typeOfCertificate: CertificateUseEnumType
}
export type PagedResultOfPartner = {
    items?: Partner[] | null
    totalCount?: number
    pageSize?: number | null
    pageIndex?: number | null
}
export type ProductVariantType = 'None' | 'Chargepoint' | 'ChargingCable' | 'MeasurementDevice'
export type Product = {
    id?: number
    description?: string | null
    serialNumberSuffix?: string | null
    name?: string | null
    manufacturer?: string | null
    productVariant?: ProductVariantType
    hardwareVersion?: string | null
    firmwareTypes?: FirmwareType[] | null
}
export type ProductOption = {
    productId?: number
    displayName?: string | null
    ocppEnabled?: boolean
    hasActiveFirmware?: boolean
    firmwareTypes?: FirmwareType[] | null
}
export type HardwareVersion = {
    id?: number
    compatibilityVersion?: number
    description?: string | null
    createdDate?: string
    productId?: number
    idAndVersion?: string | null
}
export type TemplateType =
    | 'Sql'
    | 'Hsb'
    | 'Sbc'
    | 'SqlComma'
    | 'RiksByggen'
    | 'EcoGuard'
    | 'Vitec'
    | 'TabSeparatedInvariantCulture'
    | 'CommaSeparatedInvariantCulture'
    | 'SemicolonSeparatedInvariantCulture'
    | 'SqlNoHeader'
export type FileExtensionTypes = 'Csv' | 'Txt'
export type Report = {
    id?: number
    name?: string | null
    description?: string | null
    accessLevel?: string | null
    sqlQuery?: string | null
    fileName?: string | null
    templateType?: TemplateType
    fileExtensionType?: FileExtensionTypes
}
export type AccessLevel = 'None' | 'User' | 'Admin' | 'Partner'
export type Schedule = {
    id?: number
    name?: string | null
    chargePointId?: string | null
    connectorIdList?: string | null
    active?: boolean
    startHours?: number
    startMinutes?: number
    endHours?: number
    endMinutes?: number
    timeZone?: string | null
    monday?: boolean
    tuesday?: boolean
    wednesday?: boolean
    thursday?: boolean
    friday?: boolean
    saturday?: boolean
    sunday?: boolean
    connectorIds?: number[] | null
    weekdaySummary?: string | null
    summary?: string | null
}
export type SmartChargingSchedulePeriod = {
    from?: number
    to?: number
    maxCurrent?: number
}
export type SmartChargingSchedule = {
    chargePointId?: string | null
    connectorId?: number
    validFrom?: string | null
    validTo?: string | null
    defaultCurrent?: number
    schedulePeriods?: SmartChargingSchedulePeriod[] | null
    isActive?: boolean | null
    isSynced?: boolean | null
    timeZone?: string | null
    startOfSchedule?: string | null
}
export type SyncStatuses = 'NotSynced' | 'Syncing' | 'Synced'
export type ChargePointStatusStatistics = {
    onlineCount?: number
    offlineCount?: number
    chargingCount?: number
}
export type TotalConsumptionDataPoint = {
    totalConsumptionKwh?: number | null
    timestamp?: string
}
export type ChargePointTotalConsumptionDataPoint = {
    chargePointId?: string | null
    totalConsumptionKwh?: number | null
}
export type CompanyStatistics = {
    totalConsumption?: TotalConsumptionDataPoint[] | null
    consumptionPerChargePoint?: ChargePointTotalConsumptionDataPoint[] | null
}
export type UsageStatistics = {
    chargingSessionCount?: number
    averageChargingPerSession?: number
    averageChargingTimeMinutes?: number
    totalChargingTimeHours?: number
    totalConsumption?: number
}
export type ChargePointUsageDataPoint = {
    chargePointName?: string | null
    percentage?: number
}
export type ErrorCodeTroubleshootingInfo = {
    errorCode?: string | null
    externalErrorCode?: string | null
    instructions?: string[] | null
}
export type UserSettingModel = {
    name?: string | null
    value?: string | null
}
export type UserNotification = {
    notification?: Notification | null
    userId?: string
    readTimestamp?: string | null
    isRead?: boolean
}
export type UserNotificationFilters = 'Unread' | 'Read'
export const {
    useAlarmGetAllQuery,
    useLazyAlarmGetAllQuery,
    useApiCustomerGetHashedApiKeyQuery,
    useLazyApiCustomerGetHashedApiKeyQuery,
    useApiCustomerGetAllQuery,
    useLazyApiCustomerGetAllQuery,
    useApiCustomerCreateMutation,
    useApiCustomerUpdateMutation,
    useApiCustomerDeleteMutation,
    useApiCustomerGetChargePointsQuery,
    useLazyApiCustomerGetChargePointsQuery,
    useArticleGetFilteredArticlesQuery,
    useLazyArticleGetFilteredArticlesQuery,
    useAuthLoginMutation,
    useAuthRegisterMutation,
    useAuthVerifyEmailMutation,
    useAuthAcceptInvitationMutation,
    useAuthRequestPasswordResetMutation,
    useAuthRequestPasswordMutation,
    useAuthRegisterPartnerCandidateMutation,
    useChargePointUpdateMutation,
    useChargePointUpdateChargerInformationMutation,
    useChargePointCreateFromTemplateMutation,
    useChargePointGetChargePointsQuery,
    useLazyChargePointGetChargePointsQuery,
    useChargePointGetQuery,
    useLazyChargePointGetQuery,
    useChargePointGetPartnerQuery,
    useLazyChargePointGetPartnerQuery,
    useChargePointSaveCalibrationMutation,
    useChargePointSetupCallbackMutation,
    useChargePointGetCalibrationQuery,
    useLazyChargePointGetCalibrationQuery,
    useChargePointGetNewestQuery,
    useLazyChargePointGetNewestQuery,
    useChargePointGetChargingSessionsWithCountQuery,
    useLazyChargePointGetChargingSessionsWithCountQuery,
    useChargePointDeleteChargingSessionsMutation,
    useChargePointAddToPoolMutation,
    useChargePointSetOwnerMutation,
    useChargePointRemoveOwnerMutation,
    useChargePointGetManualQuery,
    useLazyChargePointGetManualQuery,
    useChargePointRebootMutation,
    useChargePointGetRemoteStartTagsQuery,
    useLazyChargePointGetRemoteStartTagsQuery,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointGetScheduleOverrideStatusQuery,
    useLazyChargePointGetScheduleOverrideStatusQuery,
    useChargePointStartTestMutation,
    useChargePointSetProductionConfigurationMutation,
    useChargePointChangeProcessorIdMutation,
    useChargePointGetMaintenanceLogsQuery,
    useLazyChargePointGetMaintenanceLogsQuery,
    useChargePointAddMaintenanceLogMutation,
    useChargePointGetAlarmsQuery,
    useLazyChargePointGetAlarmsQuery,
    useChargePointAcknowledgeAlarmMutation,
    useChargePointDeleteRejectedTagMutation,
    useChargePointAddChargePointProductionInfoMutation,
    useChargePointGetLatestChargePointProductionInfoQuery,
    useLazyChargePointGetLatestChargePointProductionInfoQuery,
    useChargePointGetChargePointProductionInfoQuery,
    useLazyChargePointGetChargePointProductionInfoQuery,
    useChargePointDisableOcppMutation,
    useChargePointEnableOcppMutation,
    useChargePointChangeOcppServiceMutation,
    useChargePointSetOcppServiceMutation,
    useChargePointUpdateFirmwareMutation,
    useChargePointUpdateFirmware2Mutation,
    useChargePointGetOcppCommunicationLogsQuery,
    useLazyChargePointGetOcppCommunicationLogsQuery,
    useChargePointGetOcppRelayLogsQuery,
    useLazyChargePointGetOcppRelayLogsQuery,
    useChargePointResetMutation,
    useChargePointResetAvailableFirmwareVersionMutation,
    useChargePointGetStatusIntervalsQuery,
    useLazyChargePointGetStatusIntervalsQuery,
    useChargePointUpdateChargePointUserSettingsMutation,
    useChargePointClearCacheMutation,
    useChargePointUpdateConnectorAvailabilityMutation,
    useChargePointDataGetLatestChargePointDataQuery,
    useLazyChargePointDataGetLatestChargePointDataQuery,
    useChargePointDataGetChargePointDataWithFilterQuery,
    useLazyChargePointDataGetChargePointDataWithFilterQuery,
    useChargePointDataGetChargePointDataQuery,
    useLazyChargePointDataGetChargePointDataQuery,
    useChargingSessionGetConsumptionForSessionQuery,
    useLazyChargingSessionGetConsumptionForSessionQuery,
    useChargingSessionGetChargingSessionsForCurrentUserQuery,
    useLazyChargingSessionGetChargingSessionsForCurrentUserQuery,
    useChargingSessionGetChargingSessionsForLoggedInUserQuery,
    useLazyChargingSessionGetChargingSessionsForLoggedInUserQuery,
    useChargingSessionDeleteChargingSessionMutation,
    useCircuitBoardCreateCircuitBoardMutation,
    useCircuitBoardGetMacAddressMutation,
    useCircuitBoardSetExternalOcppServerMutation,
    useCompanyGetAllOrFilteredQuery,
    useLazyCompanyGetAllOrFilteredQuery,
    useCompanyCreateCompanyMutation,
    useCompanyUpdateCompanyMutation,
    useCompanyGetCompanyByIdQuery,
    useLazyCompanyGetCompanyByIdQuery,
    useCompanyDeleteCompanyMutation,
    useCompanyGetAdministratedCompaniesQuery,
    useLazyCompanyGetAdministratedCompaniesQuery,
    useCompanyGetUsersQuery,
    useLazyCompanyGetUsersQuery,
    useCompanyCreateUserMutation,
    useCompanyUpdateUserMutation,
    useCompanyGetChargePointsPagedQuery,
    useLazyCompanyGetChargePointsPagedQuery,
    useCompanyGetPartnerQuery,
    useLazyCompanyGetPartnerQuery,
    useCompanyGetReportDeliverySchedulesQuery,
    useLazyCompanyGetReportDeliverySchedulesQuery,
    useCompanyCreateReportDeliveryScheduleMutation,
    useCompanyUpdateReportDeliveryScheduleMutation,
    useCompanyDeleteReportDeliveryScheduleMutation,
    useCompanyValidateImportChargepointsMutation,
    useCompanyImportChargepointsMutation,
    useCompanyCreateImportStringQuery,
    useLazyCompanyCreateImportStringQuery,
    useCompanyGetPoolsQuery,
    useLazyCompanyGetPoolsQuery,
    useCompanyGetChargingSessionsByRangeQuery,
    useLazyCompanyGetChargingSessionsByRangeQuery,
    useCompanyGetChargingSessionsQuery,
    useLazyCompanyGetChargingSessionsQuery,
    useCompanyDeleteChargingSessionsMutation,
    useCompanyGetNfcTagsQuery,
    useLazyCompanyGetNfcTagsQuery,
    useCompanyGetLoadbalancerGroupsPagedQuery,
    useLazyCompanyGetLoadbalancerGroupsPagedQuery,
    useCompanyAddRoleToUserMutation,
    useCompanyUpdateRoleToUserMutation,
    useCompanyAddChargePointToCompanyMutation,
    useCompanyRemoveChargePointMutation,
    useCompanyRemoveUserMutation,
    useCompanyGetChargePointsWithNfcInfoQuery,
    useLazyCompanyGetChargePointsWithNfcInfoQuery,
    useCompanySetChargePointsNfcInfoMutation,
    useCompanyGetPoolsWithNfcInfoQuery,
    useLazyCompanyGetPoolsWithNfcInfoQuery,
    useCompanySetPoolsNfcInfoMutation,
    useCompanyGetChargePointsWithNfcInfoForTagQuery,
    useLazyCompanyGetChargePointsWithNfcInfoForTagQuery,
    useCompanySetChargePointsNfcInfoForTagMutation,
    useCompanyGetPoolsWithNfcInfoForTagQuery,
    useLazyCompanyGetPoolsWithNfcInfoForTagQuery,
    useCompanySetPoolsNfcInfoForTagMutation,
    useCompanyGetNfcTagsForUserQuery,
    useLazyCompanyGetNfcTagsForUserQuery,
    useCompanyGetChargePointsForUserQuery,
    useLazyCompanyGetChargePointsForUserQuery,
    useCompanyInviteExistingUserToCompanyMutation,
    useCompanyAddMeasurementDeviceToCompanyMutation,
    useCompanyGetDeviceByCompanyIdQuery,
    useLazyCompanyGetDeviceByCompanyIdQuery,
    useCompanyRemoveMeasurementDeviceMutation,
    useCompanyTransferGetPartnerTransferKeyQuery,
    useLazyCompanyTransferGetPartnerTransferKeyQuery,
    useCompanyTransferGetPartnerQuery,
    useLazyCompanyTransferGetPartnerQuery,
    useCompanyTransferGetRequestForCompanyQuery,
    useLazyCompanyTransferGetRequestForCompanyQuery,
    useCompanyTransferGetRequestsForPartnerQuery,
    useLazyCompanyTransferGetRequestsForPartnerQuery,
    useCompanyTransferGetRequestsQuery,
    useLazyCompanyTransferGetRequestsQuery,
    useCompanyTransferRequestTransferMutation,
    useCompanyTransferAcceptRequestMutation,
    useCompanyTransferRejectRequestMutation,
    useCompanyTransferBatchTransferMutation,
    useComputedNfcAccessGetAccessesForChargePointQuery,
    useLazyComputedNfcAccessGetAccessesForChargePointQuery,
    useCountrySettingsGetAllCountrySettingsQuery,
    useLazyCountrySettingsGetAllCountrySettingsQuery,
    useExternalServerGetAllQuery,
    useLazyExternalServerGetAllQuery,
    useExternalServerCreateMutation,
    useExternalServerGetByChargePointIdQuery,
    useLazyExternalServerGetByChargePointIdQuery,
    useExternalServerGetChargePointsQuery,
    useLazyExternalServerGetChargePointsQuery,
    useExternalServerGetPartnersQuery,
    useLazyExternalServerGetPartnersQuery,
    useExternalServerUpdatePartnerConnectionsMutation,
    useExternalServerDeleteMutation,
    useFirmwareGetFirmwaresByFilterQuery,
    useLazyFirmwareGetFirmwaresByFilterQuery,
    useFirmwareUploadFirmwareMutation,
    useFirmwareUpdateFirmwareMutation,
    useFirmwareDeleteFirmwareMutation,
    useFirmwareSetActiveFirmwareMutation,
    useFirmwareGetActiveFirmwareQuery,
    useLazyFirmwareGetActiveFirmwareQuery,
    useGreenChargingGetLoadBalancerGroupQuery,
    useLazyGreenChargingGetLoadBalancerGroupQuery,
    useGreenChargingChargePointStatusQuery,
    useLazyGreenChargingChargePointStatusQuery,
    useGreenChargingUpdateLoadBalancerGroupMutation,
    useInstallationSetupMutation,
    useLoadBalancerGroupGetAllPagedQuery,
    useLazyLoadBalancerGroupGetAllPagedQuery,
    useLoadBalancerGroupCreateMutation,
    useLoadBalancerGroupUpdateMutation,
    useLoadBalancerGroupDeleteMutation,
    useLoadBalancerGroupGetByIdQuery,
    useLazyLoadBalancerGroupGetByIdQuery,
    useLoadBalancerGroupGetChargePointsForGroupQuery,
    useLazyLoadBalancerGroupGetChargePointsForGroupQuery,
    useLoadBalancerGroupGetCurrentLimitSchemeQuery,
    useLazyLoadBalancerGroupGetCurrentLimitSchemeQuery,
    useLoadBalancerGroupInsertOrUpdateCurrentLimitSchemeMutation,
    useLoadBalancerGroupDisableGroupMutation,
    useLoadBalancerGroupEnableGroupMutation,
    useLoadBalancerGroupValidateChargePointImportsMutation,
    useLoadBalancerGroupImportChargePointsMutation,
    useLoadBalancerGroupImportChargePointsV2Mutation,
    useLoadBalancerGroupDeleteChargePointFromGroupMutation,
    useLoadBalancerGroupGetSnapshotByDeviceIdQuery,
    useLazyLoadBalancerGroupGetSnapshotByDeviceIdQuery,
    useLogCreateLogMutation,
    useLogGetFilteredLogsQuery,
    useLazyLogGetFilteredLogsQuery,
    useLogGetLogQuery,
    useLazyLogGetLogQuery,
    useLogGetErrorsQuery,
    useLazyLogGetErrorsQuery,
    useLogGetByLatestChargePointExcludingOcpp2Query,
    useLazyLogGetByLatestChargePointExcludingOcpp2Query,
    useLogGetByLatestChargePointExcludingOcppQuery,
    useLazyLogGetByLatestChargePointExcludingOcppQuery,
    useMeasurementDevicesGetFilteredQuery,
    useLazyMeasurementDevicesGetFilteredQuery,
    useMeasurementDevicesCreateMutation,
    useMeasurementDevicesGetByIdQuery,
    useLazyMeasurementDevicesGetByIdQuery,
    useMeasurementDevicesUpdateMutation,
    useMeasurementDevicesGetDevicesByUserIdQuery,
    useLazyMeasurementDevicesGetDevicesByUserIdQuery,
    useMeasurementDevicesRemoveMeasurementDeviceFromCompanyMutation,
    useMeasurementDevicesGetLatestMeasurementQuery,
    useLazyMeasurementDevicesGetLatestMeasurementQuery,
    useMeasurementDevicesGetMeasurementsForLatest24HoursQuery,
    useLazyMeasurementDevicesGetMeasurementsForLatest24HoursQuery,
    useMeasurementDevicesGetValidatedMeasurementDeviceQuery,
    useLazyMeasurementDevicesGetValidatedMeasurementDeviceQuery,
    useMigrationBackupMigrationStateMutation,
    useMigrationShouldMigrateScheduleQuery,
    useLazyMigrationShouldMigrateScheduleQuery,
    useNfcTagCreateMutation,
    useNfcTagUpdateMutation,
    useNfcTagConnectToUserMutation,
    useNfcTagDisconnectFromUserMutation,
    useNfcTagGetAccessedChargePointsInCompanyQuery,
    useLazyNfcTagGetAccessedChargePointsInCompanyQuery,
    useNfcTagGetAccessedPoolsInCompanyQuery,
    useLazyNfcTagGetAccessedPoolsInCompanyQuery,
    useNfcTagDeleteMutation,
    useNfcTagGetRejectedTagsQuery,
    useLazyNfcTagGetRejectedTagsQuery,
    useNotificationGetAllOrFilteredQuery,
    useLazyNotificationGetAllOrFilteredQuery,
    useNotificationCreateNotificationMutation,
    useNotificationGetByIdQuery,
    useLazyNotificationGetByIdQuery,
    useNotificationUpdateNotificationMutation,
    useNotificationDeleteNotificationMutation,
    useNotificationPublishNotificationMutation,
    useOcppCommandResetMutation,
    useOcppCommandClearCacheMutation,
    useOcppCommandDataTransferMutation,
    useOcppCommandGetConfigurationMutation,
    useOcppCommandGetDiagnosticsMutation,
    useOcppCommandUnlockConnectorMutation,
    useOcppCommandChangeAvailabilityMutation,
    useOcppCommandRemoteStartMutation,
    useOcppCommandRemoteStopMutation,
    useOcppCommandTriggerMessageMutation,
    useOcppCommandUpdateFirmwareMutation,
    useOcppCommandTestMutation,
    useOcppCommandUpdateConfigKeysMutation,
    useOcppCommandUpdateConfigKeyMutation,
    useOcppCommandInstallCertificateMutation,
    useOcppCommandDeleteCertificateMutation,
    useOcppCommandGetInstalledCertsMutation,
    usePartnerGetAllAllQuery,
    useLazyPartnerGetAllAllQuery,
    usePartnerCreatePartnerMutation,
    usePartnerUpdatePartnerMutation,
    usePartnerGetAllQuery,
    useLazyPartnerGetAllQuery,
    usePartnerGetByIdQuery,
    useLazyPartnerGetByIdQuery,
    usePartnerDeleteMutation,
    usePartnerGetCompaniesByPartnerIdQuery,
    useLazyPartnerGetCompaniesByPartnerIdQuery,
    usePartnerGetChargePointsPagedQuery,
    useLazyPartnerGetChargePointsPagedQuery,
    usePartnerGetAlarmsByPartnerIdQuery,
    useLazyPartnerGetAlarmsByPartnerIdQuery,
    usePartnerSetAcknowledgedMutation,
    usePartnerGetUsersByPartnerIdQuery,
    useLazyPartnerGetUsersByPartnerIdQuery,
    usePartnerCreatePartnerUserMutation,
    usePartnerUpdatePartnerUserMutation,
    usePartnerAddUserAsPartnerMutation,
    usePartnerRemoveUserAsPartnerMutation,
    usePartnerGetLoadbalancerGroupsPagedQuery,
    useLazyPartnerGetLoadbalancerGroupsPagedQuery,
    usePartnerGetNfcTagsQuery,
    useLazyPartnerGetNfcTagsQuery,
    usePartnerGetExternalServersQuery,
    useLazyPartnerGetExternalServersQuery,
    usePartnerGetPartnerCandidatesQuery,
    useLazyPartnerGetPartnerCandidatesQuery,
    usePartnerApprovePartnerCandidateMutation,
    usePartnerDeletePartnerCandidateMutation,
    usePoolCreateMutation,
    usePoolUpdateMutation,
    usePoolGetByIdQuery,
    useLazyPoolGetByIdQuery,
    usePoolConnectToUserMutation,
    usePoolDisconnectFromUserMutation,
    usePoolDeletePoolMutation,
    usePoolGetChargePointsQuery,
    useLazyPoolGetChargePointsQuery,
    useProductGetAllQuery,
    useLazyProductGetAllQuery,
    useProductGetOptionsQuery,
    useLazyProductGetOptionsQuery,
    useProductGetHardwareversionsQuery,
    useLazyProductGetHardwareversionsQuery,
    useProductGetActiveHardwareVersionQuery,
    useLazyProductGetActiveHardwareVersionQuery,
    useProductSetActiveHardwareVersionMutation,
    useReportGetReportsQuery,
    useLazyReportGetReportsQuery,
    useReportGetReportsForAdminQuery,
    useLazyReportGetReportsForAdminQuery,
    useReportGetReportsForPartnerQuery,
    useLazyReportGetReportsForPartnerQuery,
    useReportGetReportDataQuery,
    useLazyReportGetReportDataQuery,
    useScheduleGetByIdQuery,
    useLazyScheduleGetByIdQuery,
    useScheduleDeleteScheduleMutation,
    useScheduleGetByChargePointQuery,
    useLazyScheduleGetByChargePointQuery,
    useScheduleCreateScheduleMutation,
    useScheduleUpdateScheduleMutation,
    useScheduleCopyScheduleFromChargePointMutation,
    useSimulatedChargePointCreateSimulatedMutation,
    useSmartChargingScheduleGetQuery,
    useLazySmartChargingScheduleGetQuery,
    useSmartChargingScheduleClearMutation,
    useSmartChargingScheduleGetByChargePointQuery,
    useLazySmartChargingScheduleGetByChargePointQuery,
    useSmartChargingScheduleGetChargePointScheduleStatusQuery,
    useLazySmartChargingScheduleGetChargePointScheduleStatusQuery,
    useSmartChargingScheduleUpdateMutation,
    useStatisticsGetChargePointStatusStatsForAllQuery,
    useLazyStatisticsGetChargePointStatusStatsForAllQuery,
    useStatisticsGetChargePointTotalConsumptionLastSevenDaysQuery,
    useLazyStatisticsGetChargePointTotalConsumptionLastSevenDaysQuery,
    useStatisticsGetChargePointStatusStatsForPartnerQuery,
    useLazyStatisticsGetChargePointStatusStatsForPartnerQuery,
    useStatisticsGetUserTotalConsumptionQuery,
    useLazyStatisticsGetUserTotalConsumptionQuery,
    useStatisticsGetChargePointStatusStatsForCompanyQuery,
    useLazyStatisticsGetChargePointStatusStatsForCompanyQuery,
    useStatisticsGetCompanyTotalConsumptionQuery,
    useLazyStatisticsGetCompanyTotalConsumptionQuery,
    useStatisticsGetUserUsageStatisticsQuery,
    useLazyStatisticsGetUserUsageStatisticsQuery,
    useStatisticsGetUserChargerUsageStatisticsQuery,
    useLazyStatisticsGetUserChargerUsageStatisticsQuery,
    useStatisticsGetCompanyUsageStatisticsQuery,
    useLazyStatisticsGetCompanyUsageStatisticsQuery,
    useTroubleshootingGetErrorCodeTroubleshootingInfosQuery,
    useLazyTroubleshootingGetErrorCodeTroubleshootingInfosQuery,
    useTroubleshootingGetErrorCodeTroubleshootingInfoQuery,
    useLazyTroubleshootingGetErrorCodeTroubleshootingInfoQuery,
    useUserGetFilteredQuery,
    useLazyUserGetFilteredQuery,
    useUserUpdateLoggedInUserMutation,
    useUserGetUserByIdQuery,
    useLazyUserGetUserByIdQuery,
    useUserGetUserSettingsQuery,
    useLazyUserGetUserSettingsQuery,
    useUserUpdateUserSettingsMutation,
    useUserLoggedInUserIsCompanyMemberQuery,
    useLazyUserLoggedInUserIsCompanyMemberQuery,
    useUserGetPoolsForSelectedCompanyQuery,
    useLazyUserGetPoolsForSelectedCompanyQuery,
    useUserGetForUserByNfcAccessWithNfcAccessInfoQuery,
    useLazyUserGetForUserByNfcAccessWithNfcAccessInfoQuery,
    useUserGetOwnedChargePointsQuery,
    useLazyUserGetOwnedChargePointsQuery,
    useUserGetOwnedChargePointsWithIdsQuery,
    useLazyUserGetOwnedChargePointsWithIdsQuery,
    useUserIsUserHasChargePointsQuery,
    useLazyUserIsUserHasChargePointsQuery,
    useUserGetUserNotificationsQuery,
    useLazyUserGetUserNotificationsQuery,
    useUserGetOwnNfcTagsQuery,
    useLazyUserGetOwnNfcTagsQuery,
    useUserUpdateOwnNfcTagsMutation,
    useUserUpdateUserGreenChargingSettingMutation,
    useUserRemoveChargePointAccessMutation,
    useUserGetLatestRejectedTagQuery,
    useLazyUserGetLatestRejectedTagQuery,
    useUserSetAllNotificationsToReadForUserMutation,
    useUserSetNotificationToReadForUserMutation,
    useUserAcceptTermsMutation,
    useUserIsFavouriteQuery,
    useLazyUserIsFavouriteQuery,
    useUserAddFavouriteMutation,
    useUserRemoveFavouriteMutation,
    useUserGetFavouritesQuery,
    useLazyUserGetFavouritesQuery,
    useUserActivateUserMutation,
    useUserGetPartnerCandidateQuery,
    useLazyUserGetPartnerCandidateQuery,
} = injectedRtkApi
